import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { 
  BarChart3, 
  FileText, 
  Download,
  Users,
  UserPlus,
  Building,
  TrendingUp,
  ArrowLeft,
  Loader2,
  Target,
  Globe,
  Star,
  Check,
  AlertCircle,
  Lock,
  Activity
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { IndividualRadarChart, CompanyRadarChart, ComparisonRadarChart, MultipleEmployeesRadarChart, DetailedComparisonRadarChart } from './RadarChart';
import ReportInterpretation, { ComparisonInterpretation } from './ReportInterpretation';
import AdvancedComparisonSection from './AdvancedComparisonSection';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import IPSGauge from './tabs/IPSGauge';
import logger from '../utils/logger';

// HR Indicators Section Component - Displays HR metrics with interpretations
const HRIndicatorsSection = ({ hrData, loading: isLoading }) => {
  const { language } = useLanguage();
  if (isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin mr-2" style={{ color: '#1A878D' }} />
          <span className="text-gray-500">{language === 'es' ? 'Cargando indicadores...' : 'Loading indicators...'}</span>
        </CardContent>
      </Card>
    );
  }

  if (!hrData || !hrData.indicators) {
    return null;
  }

  const indicators = hrData.indicators;
  const ipsData = hrData.ips;
  const drivers = hrData.drivers || {};
  const indicatorOrder = ['irr', 'ico', 'iap', 'ipc', 'icf', 'ipe', 'ilo'];

  const LEVEL_I18N = {
    CRITICO: { es: 'CRITICO', en: 'CRITICAL' },
    ALTO: { es: 'ALTO', en: 'HIGH' },
    ALTO_POTENCIAL: { es: 'ALTO POTENCIAL', en: 'HIGH POTENTIAL' },
    MUY_ALTO: { es: 'MUY ALTO', en: 'VERY HIGH' },
    MUY_ALTA: { es: 'MUY ALTA', en: 'VERY HIGH' },
    POTENCIAL_SOLIDO: { es: 'POTENCIAL SOLIDO', en: 'SOLID POTENTIAL' },
    BUENO: { es: 'BUENO', en: 'GOOD' },
    MODERADO: { es: 'MODERADO', en: 'MODERATE' },
    MEDIO: { es: 'MEDIO', en: 'MEDIUM' },
    MEDIA: { es: 'MEDIA', en: 'MEDIUM' },
    ACEPTABLE: { es: 'ACEPTABLE', en: 'ACCEPTABLE' },
    POTENCIAL_MODERADO: { es: 'POTENCIAL MODERADO', en: 'MODERATE POTENTIAL' },
    BAJO: { es: 'BAJO', en: 'LOW' },
    BAJA: { es: 'BAJA', en: 'LOW' },
    POTENCIAL_LIMITADO: { es: 'POTENCIAL LIMITADO', en: 'LIMITED POTENTIAL' },
    POBRE: { es: 'POBRE', en: 'POOR' },
    EXCELENTE: { es: 'EXCELENTE', en: 'EXCELLENT' },
  };

  // Color logic based on indicator type
  // IRR: inverted (lower is better - low risk)
  // All others: normal (higher is better)
  const getIndicatorColor = (key, value) => {
    if (key === 'irr') {
      // IRR: LOW = good (green), HIGH = bad (red)
      if (value >= 60) return { topBorder: 'border-t-red-500', text: 'text-red-600', bar: 'bg-red-500' };
      if (value >= 40) return { topBorder: 'border-t-yellow-500', text: 'text-yellow-600', bar: 'bg-yellow-500' };
      return { topBorder: 'border-t-green-500', text: 'text-green-600', bar: 'bg-green-500' };
    } else {
      // All others: HIGH = good (green), LOW = bad (red)
      if (value >= 70) return { topBorder: 'border-t-green-500', text: 'text-green-600', bar: 'bg-green-500' };
      if (value >= 40) return { topBorder: 'border-t-yellow-500', text: 'text-yellow-600', bar: 'bg-yellow-500' };
      return { topBorder: 'border-t-red-500', text: 'text-red-600', bar: 'bg-red-500' };
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center">
          <TrendingUp className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
          {language === 'es' ? 'Indicadores de Recursos Humanos' : 'HR Indicators'}
        </CardTitle>
        <CardDescription>
          {language === 'es' 
            ? 'Métricas clave para la gestión del talento y toma de decisiones' 
            : 'Key metrics for talent management and decision making'}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* IPS Gauge - Prominent display at top */}
        {ipsData && (
          <div className="mb-6 flex justify-center">
            <IPSGauge
              ips={ipsData.score}
              ipsLevel={ipsData.level}
              levelColor={ipsData.color}
              description={language === 'es' ? ipsData.description_es : ipsData.description_en}
              recommendedAction={language === 'es' ? ipsData.action_es : ipsData.action_en}
              breakdown={ipsData.breakdown}
              showBreakdown={true}
              size="medium"
              showCard={false}
            />
          </div>
        )}
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {indicatorOrder.map((key) => {
            const ind = indicators[key];
            if (!ind) return null;
            
            const colors = getIndicatorColor(key, ind.value);
            const name = language === 'es' ? ind.name_es : ind.name_en;
            const meaning = language === 'es' ? ind.level_meaning_es : ind.level_meaning_en;
            const rawLevel = ind.level || '';
            const levelLabel = LEVEL_I18N[rawLevel]?.[language] || rawLevel.replace(/_/g, ' ');
            const indDrivers = drivers[key] || [];

            return (
              <div 
                key={key} 
                className={`bg-white border border-gray-200 rounded-lg shadow-sm overflow-hidden border-t-4 ${colors.topBorder}`}
                data-testid={`indicator-card-${key}`}
              >
                <div className="p-4">
                  {/* Header with name and value */}
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-sm text-gray-800 leading-tight">
                      {name}
                    </h4>
                    <span className={`text-2xl font-bold ${colors.text}`}>
                      {ind.value}
                    </span>
                  </div>
                  
                  {/* Progress bar */}
                  <div className="w-full bg-gray-100 rounded-full h-1.5 mb-3">
                    <div 
                      className={`h-1.5 rounded-full ${colors.bar}`}
                      style={{ width: `${Math.min(ind.value, 100)}%` }}
                    />
                  </div>
                  
                  {/* Level badge */}
                  <div className="mb-3">
                    <span className={`inline-block px-2 py-0.5 text-xs font-medium rounded ${colors.text} bg-gray-50 border border-gray-200`}>
                      {levelLabel}
                    </span>
                  </div>
                  
                  {/* Interpretation */}
                  {meaning && (
                    <p className="text-xs text-gray-600 leading-relaxed">
                      {meaning}
                    </p>
                  )}
                  
                  {/* Drivers - Why this result */}
                  {indDrivers && indDrivers.length > 0 && (
                    <DriverToggle drivers={indDrivers} indicatorKey={key} language={language} />
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

const DriverToggle = ({ drivers, indicatorKey, language }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="mt-3 pt-2 border-t border-gray-100">
      <button
        onClick={() => setOpen(!open)}
        className="text-xs font-medium flex items-center gap-1 cursor-pointer"
        style={{ color: '#1A878D' }}
        data-testid={`drivers-toggle-${indicatorKey}`}
      >
        <AlertCircle className="h-3 w-3" />
        {language === 'es' ? 'Por que este resultado?' : 'Why this result?'}
        <span className="text-[10px] ml-0.5">{open ? '\u25B2' : '\u25BC'}</span>
      </button>
      {open && (
        <div className="mt-2 space-y-2 pl-2 border-l-2 border-gray-200" data-testid={`drivers-list-${indicatorKey}`}>
          {drivers.map((d, i) => {
            const effectColor = d.effect === 'green' ? '#22c55e' : d.effect === 'red' ? '#ef4444' : '#9ca3af';
            const signalText = typeof d.signal === 'object' ? (d.signal[language] || d.signal.es) : d.signal;
            const detailText = typeof d.detail === 'object' ? (d.detail[language] || d.detail.es) : d.detail;
            return (
              <div key={i}>
                <div className="flex items-start gap-1.5">
                  <span style={{ color: effectColor, fontSize: '8px', marginTop: '4px' }}>{'\u25CF'}</span>
                  <span className="text-xs font-medium text-gray-700">{signalText}</span>
                </div>
                {detailText && <p className="text-[11px] text-gray-500 ml-3.5">{detailText}</p>}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const Reports = () => {
  const { user } = useAuth();
  const { language, t, changeLanguage } = useLanguage();
  const { hasAccess } = useSubscription();
  const navigate = useNavigate();
  const { employeeId } = useParams();
  const [loading, setLoading] = useState(true);
  const [companyReport, setCompanyReport] = useState(null);
  const [employeeReport, setEmployeeReport] = useState(null);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [companyRadarData, setCompanyRadarData] = useState(null);
  const [individualRadarData, setIndividualRadarData] = useState(null);
  const [companyEmployees, setCompanyEmployees] = useState([]);
  const [selectedEmployees, setSelectedEmployees] = useState([]);  // Para selección múltiple
  const [multipleRadarData, setMultipleRadarData] = useState([]);   // Datos de múltiples empleados
  const [interpretation, setInterpretation] = useState(null);        // Interpretaciones de texto
  const [candidates, setCandidates] = useState([]);                 // Lista de candidatos
  const [selectedCandidate, setSelectedCandidate] = useState(null); // Candidato seleccionado
  const [companyProfileData, setCompanyProfileData] = useState(null); // Datos del perfil propio de la empresa
  const [selectedDepartment, setSelectedDepartment] = useState('all'); // Filtro de departamento
  const [comparisonType, setComparisonType] = useState('company_offers_vs_employee_offers'); // Tipo de comparación detallada
  const [exportingPDF, setExportingPDF] = useState(false); // Loading state for PDF export
  const [candidateRadarData, setCandidateRadarData] = useState(null); // Candidate radar chart data
  const [candidateInterpretation, setCandidateInterpretation] = useState(null); // Candidate interpretation
  const [employeeHRIndicators, setEmployeeHRIndicators] = useState(null); // HR Indicators for selected employee
  const [candidateHRIndicators, setCandidateHRIndicators] = useState(null); // HR Indicators for selected candidate
  const [companyHRIndicators, setCompanyHRIndicators] = useState(null); // HR Indicators for company (averages)
  const [loadingHRIndicators, setLoadingHRIndicators] = useState(false); // Loading state for HR indicators
  const [loadingCompanyHRIndicators, setLoadingCompanyHRIndicators] = useState(false); // Loading state for company HR indicators
  const [employeeEnneagram, setEmployeeEnneagram] = useState(null); // Enneagram results for selected employee
  const [loadingEnneagram, setLoadingEnneagram] = useState(false); // Loading state for enneagram
  
  // Check PDF export permission
  const canExportPDF = hasAccess('pdf_export');
  const canViewAdvancedReports = hasAccess('advanced_reports');
  const canViewHRIndicators = hasAccess('hr_indicators');
  
  // Refs for PDF export
  const companyReportRef = useRef(null);
  const employeeReportRef = useRef(null);
  const candidateReportRef = useRef(null);

  // Initial data loading - optimized with Promise.all
  useEffect(() => {
    if (user?.rol !== 'company_admin') {
      navigate('/dashboard');
      return;
    }
    
    // Load all data in parallel for better performance
    const loadInitialData = async () => {
      setLoading(true);
      try {
        await Promise.all([
          loadReports(),
          loadCompanyEmployees(),
          loadCandidates(),
          loadCompanyProfileReport(),
          loadCompanyHRIndicators()
        ]);
      } catch (error) {
        logger.error('[Reports] Error loading initial data:', error);
      } finally {
        setLoading(false);
      }
    };
    
    loadInitialData();
  }, [user, navigate, employeeId]);

  // Reload language-dependent data when language changes
  useEffect(() => {
    if (user?.rol === 'company_admin' && language && !loading) {
      // Only reload language-dependent endpoints
      Promise.all([
        loadReports(),
        loadCompanyProfileReport()
      ]).catch(err => logger.error('[Reports] Error reloading on language change:', err));
    }
  }, [language]);

  // Clean up radar data when no employees selected
  useEffect(() => {
    if (selectedEmployees.length === 0) {
      setMultipleRadarData([]);
      setInterpretation(null); // Clear interpretation too
    } else {
      loadComparisonInterpretation();
    }
  }, [selectedEmployees, comparisonType]);

  // Load comparison interpretation
  const loadCandidateProfile = async (candidateId, candidateName) => {
    try {
      setSelectedCandidate({
        id: candidateId,
        name: candidateName,
        loading: true
      });
      
      const response = await axios.get(`/reports/individual/${candidateId}/radar?language=${language}`);
      if (response.data.success) {
        setSelectedCandidate({
          id: candidateId,
          name: candidateName,
          radar_data: response.data.data.radar_chart,
          scores: response.data.data.scores,
          loading: false
        });
      }
    } catch (error) {
      logger.error('Error loading candidate profile:', error);
      setSelectedCandidate({
        id: candidateId,
        name: candidateName,
        error: 'Failed to load profile',
        loading: false
      });
    }
  };

  const loadComparisonInterpretation = async () => {
    if (selectedEmployees.length > 0) {
      try {
        const endpoint = comparisonType === 'company_profile' 
          ? `/comparisons/employees-vs-company-profile`
          : `/comparisons/employees-vs-company-average`;
        
        const response = await axios.post(endpoint, {
          employee_ids: selectedEmployees,
          comparison_type: comparisonType
        });
        
        if (response.data.success) {
          setInterpretation(response.data.data.interpretation);
        }
      } catch (error) {
        logger.error('Error loading comparison interpretation:', error);
      }
    }
  };

  // Load company-wide HR indicators
  const loadCompanyHRIndicators = async () => {
    if (!user?.empresa_id) return;
    
    setLoadingCompanyHRIndicators(true);
    try {
      const response = await axios.get(`/v2/hr-indicators/company/${user.empresa_id}`);
      if (response.data.success) {
        setCompanyHRIndicators(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading company HR indicators:', error);
    } finally {
      setLoadingCompanyHRIndicators(false);
    }
  };

  const loadCandidates = async () => {
    try {
      const response = await axios.get('/companies/employees-and-candidates');
      if (response.data.success) {
        // Filter only candidates with completed assessments
        const candidatesData = response.data.data
          .filter(user => user.rol === 'candidate' && user.completed_assessments > 0)
          .map(candidate => ({
            id: candidate.id,
            name: candidate.nombre,
            email: candidate.email,
            completed_assessments: candidate.completed_assessments,
            has_valid_assessment: candidate.completed_assessments > 0
          }));
        setCandidates(candidatesData);
      }
    } catch (error) {
      logger.error('Error loading candidates:', error);
    }
  };

  const loadIndividualInterpretation = async (userId) => {
    try {
      // Get the user's latest completed assessment
      const assessmentResponse = await axios.get(`/users/${userId}/latest-assessment`);
      
      if (assessmentResponse.data.success) {
        const assessmentId = assessmentResponse.data.data.assessment_id;
        const response = await axios.get(`/reports/individual/${assessmentId}/interpreted?language=${language}`);
        if (response.data.success) {
          setInterpretation(response.data.data.interpretation);
        }
        // Also load HR indicators for this assessment
        loadEmployeeHRIndicators(assessmentId);
      }
    } catch (error) {
      logger.error('Error loading interpretation:', error);
    }
  };

  // Load HR Indicators for an employee's assessment
  const loadEmployeeHRIndicators = async (assessmentId) => {
    setLoadingHRIndicators(true);
    try {
      const response = await axios.get(`/v2/hr-indicators/${assessmentId}`);
      if (response.data.success) {
        setEmployeeHRIndicators(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading HR indicators:', error);
      setEmployeeHRIndicators(null);
    } finally {
      setLoadingHRIndicators(false);
    }
  };

  // Load HR Indicators for a candidate's assessment
  const loadCandidateHRIndicators = async (assessmentId) => {
    setLoadingHRIndicators(true);
    try {
      const response = await axios.get(`/v2/hr-indicators/${assessmentId}`);
      if (response.data.success) {
        setCandidateHRIndicators(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading HR indicators:', error);
      setCandidateHRIndicators(null);
    } finally {
      setLoadingHRIndicators(false);
    }
  };

  const loadCompanyProfileReport = async () => {
    try {
      const response = await axios.get(`/reports/company/profile?language=${language}`); // Company's own assessment with language
      if (response.data.success) {
        setCompanyProfileData(response.data.data);
      } else {
        toast.error('Error loading company profile: ' + response.data.message);
      }
    } catch (error) {
      logger.error('Error loading company profile:', error);
      toast.error('Failed to load company profile: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Get comparison data - always use all departments for comparison
  const getComparisonData = () => {
    if (!companyRadarData) return null;

    // Always use overall average (all departments) for comparison charts
    return {
      employees_average: {
        ofertas: companyRadarData.radar_chart.datasets[0].data,
        requerimientos: companyRadarData.radar_chart.datasets[1].data
      }
    };
  };

  const loadComparison = async (type, id1, id2) => {
    try {
      let endpoint;
      switch (type) {
        case 'employee_vs_company':
          endpoint = `/comparisons/company/${user.empresa_id}/employee/${id1}`;
          break;
        case 'candidate_vs_company':
          endpoint = `/comparisons/company/${user.empresa_id}/candidate/${id1}`;
          break;
        case 'employee_vs_candidate':
          endpoint = `/comparisons/employee/${id1}/candidate/${id2}`;
          break;
        default:
          return;
      }
      
      const response = await axios.get(endpoint);
      if (response.data.success) {
        // Data is now handled by getComparisonData function
      }
    } catch (error) {
      logger.error('Error loading comparison:', error);
    }
  };

  const loadCompanyEmployees = async () => {
    try {
      const response = await axios.get('/companies/employees/detailed');
      if (response.data.success) {
        setCompanyEmployees(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading company employees:', error);
    }
  };

  const loadReports = async () => {
    try {
      setLoading(true);
      
      // Load company radar data
      const companyRadarResponse = await axios.get(`/reports/company/radar?language=${language}`);
      if (companyRadarResponse.data.success) {
        setCompanyRadarData(companyRadarResponse.data.data);
      }
      
      // Load company report (legacy format for stats)
      const companyResponse = await axios.get('/reports/company');
      if (companyResponse.data.success) {
        setCompanyReport(companyResponse.data.data);
      }
      
      // If specific employee requested, load their radar data
      if (employeeId) {
        await loadEmployeeRadarData(employeeId);
      }
    } catch (error) {
      logger.error('Error loading reports:', error);
      toast.error(t('reports.error_loading', 'Error loading reports'));
    } finally {
      setLoading(false);
    }
  };

  const loadEmployeeRadarData = async (empId) => {
    try {
      // Get employee's completed assessments using the new endpoint
      const response = await axios.get(`/companies/employees/${empId}/completed-assessments`);
      if (response.data.success && response.data.data.completed_assessments.length > 0) {
        const assessments = response.data.data.completed_assessments;
        
        // Use the most recent completed assessment
        const latestAssessment = assessments[0]; // Already ordered by fecha_completado DESC
        
        const radarResponse = await axios.get(`/reports/individual/${latestAssessment.id}/radar?language=${language}`);
        if (radarResponse.data.success) {
          return radarResponse.data.data;
        }
      }
      
      const employee = companyEmployees.find(emp => emp.id === empId);
      toast.error(
        `${employee?.nombre || (language === 'es' ? 'Empleado' : 'Employee')} ${t('reports.no_valid_assessments', 'has no valid completed assessments')}`
      );
      return null;
    } catch (error) {
      logger.error('Error loading employee radar data:', error);
      toast.error(t('reports.error_employee_data', 'Error loading employee data'));
      return null;
    }
  };

  const handleEmployeeSelection = async (empId) => {
    const radarData = await loadEmployeeRadarData(empId);
    if (radarData) {
      setIndividualRadarData(radarData);
      setSelectedEmployee(empId);
      loadIndividualInterpretation(empId); // Load interpretation too
    }
  };

  const handleMultipleEmployeeToggle = async (empId) => {
    const isSelected = selectedEmployees.includes(empId);
    
    if (isSelected) {
      // Remove employee from selection
      const newSelected = selectedEmployees.filter(id => id !== empId);
      setSelectedEmployees(newSelected);
      setMultipleRadarData(prev => prev.filter(data => data.employee_id !== empId));
    } else {
      // Add employee to selection
      const radarData = await loadEmployeeRadarData(empId);
      if (radarData) {
        const newSelected = [...selectedEmployees, empId];
        setSelectedEmployees(newSelected);
        setMultipleRadarData(prev => [...prev, { ...radarData, employee_id: empId }]);
      }
    }
  };

  const clearMultipleSelection = () => {
    setSelectedEmployees([]);
    setMultipleRadarData([]);
  };

  const handleCandidateSelection = async (candidateId) => {
    try {
      // Get the candidate's latest completed assessment
      const assessmentResponse = await axios.get(`/users/${candidateId}/latest-assessment`);
      
      if (assessmentResponse.data.success) {
        const assessmentId = assessmentResponse.data.data.assessment_id;
        
        // Load radar chart data
        const radarResponse = await axios.get(`/reports/individual/${assessmentId}/radar?language=${language}`);
        if (radarResponse.data.success) {
          setCandidateRadarData(radarResponse.data.data);
          setSelectedCandidate(candidateId);
          loadCandidateInterpretation(assessmentId);
        }
      }
    } catch (error) {
      logger.error('Error loading candidate radar data:', error);
      if (error.response?.status === 404) {
        toast.error(t('reports.no_completed_assessments', 'No completed assessments found'));
      } else {
        toast.error(t('reports.error_candidate_data', 'Error loading candidate data'));
      }
    }
  };

  const loadCandidateInterpretation = async (assessmentId) => {
    try {
      const response = await axios.get(`/reports/individual/${assessmentId}/interpreted?language=${language}`);
      if (response.data.success) {
        setCandidateInterpretation(response.data.data.interpretation);
      }
      // Also load HR indicators for the candidate
      loadCandidateHRIndicators(assessmentId);
    } catch (error) {
      logger.error('Error loading candidate interpretation:', error);
    }
  };

  const loadEmployeeReport = async (empId) => {
    try {
      const response = await axios.get(`/reports/employee/${empId}`);
      if (response.data.success) {
        setEmployeeReport(response.data.data);
        setSelectedEmployee(empId);
      }
    } catch (error) {
      toast.error(t('reports.error_employee_report', 'Error loading employee report'));
    }
  };

  // Load Enneagram data for an employee
  const loadEmployeeEnneagram = async (userId) => {
    setLoadingEnneagram(true);
    setEmployeeEnneagram(null);
    try {
      // For admin viewing employee profile, get the specific employee's enneagram results
      // We need to query enneagram assessments for this specific user
      const response = await axios.get(`/enneagram/assessments?user_id=${userId}`);
      
      if (response.data.success && response.data.data.assessments.length > 0) {
        const completedAssessments = response.data.data.assessments.filter(a => a.status === 'completado');
        
        if (completedAssessments.length > 0) {
          // Get the results for the completed assessment
          const resultsResponse = await axios.get(`/enneagram/assessments/${completedAssessments[0].id}/results?language=${language}`);
          
          if (resultsResponse.data.success) {
            setEmployeeEnneagram({
              has_enneagram: true,
              results: resultsResponse.data.data
            });
          } else {
            setEmployeeEnneagram({ has_enneagram: false });
          }
        } else {
          setEmployeeEnneagram({ has_enneagram: false });
        }
      } else {
        setEmployeeEnneagram({ has_enneagram: false });
      }
    } catch (error) {
      logger.error('Error loading enneagram data:', error);
      // If API not accessible for this user, show as not completed
      setEmployeeEnneagram({ has_enneagram: false, error: true });
    } finally {
      setLoadingEnneagram(false);
    }
  };

  // Enneagram Type Colors and Icons for display
  const ENNEAGRAM_TYPES = {
    1: { name: { es: 'El Perfeccionista', en: 'The Perfectionist' }, icon: '🎯', color: '#6366F1' },
    2: { name: { es: 'El Ayudador', en: 'The Helper' }, icon: '❤️', color: '#EC4899' },
    3: { name: { es: 'El Triunfador', en: 'The Achiever' }, icon: '🏆', color: '#F59E0B' },
    4: { name: { es: 'El Individualista', en: 'The Individualist' }, icon: '🎨', color: '#8B5CF6' },
    5: { name: { es: 'El Investigador', en: 'The Investigator' }, icon: '🔬', color: '#06B6D4' },
    6: { name: { es: 'El Leal', en: 'The Loyalist' }, icon: '🛡️', color: '#78716C' },
    7: { name: { es: 'El Entusiasta', en: 'The Enthusiast' }, icon: '🎉', color: '#22C55E' },
    8: { name: { es: 'El Desafiador', en: 'The Challenger' }, icon: '⚡', color: '#EF4444' },
    9: { name: { es: 'El Pacificador', en: 'The Peacemaker' }, icon: '☮️', color: '#64748B' }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US');
  };

  const formatScore = (score) => {
    return Math.round(score || 0);
  };

  const getScoreColor = (score) => {
    if (score >= 80) return 'text-green-600';
    if (score >= 60) return 'text-yellow-600';
    return 'text-red-600';
  };

  // Helper function to load logo for PDF
  const loadLogoForPDF = () => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = () => resolve(null);
      img.src = '/images/mevia-logo-horizontal.png';
    });
  };

  // MEVIA brand colors
  const MEVIA_COLORS = {
    teal: '#1A878D',
    tealDark: '#0B6A7D',
    gold: '#D4AF37'
  };

  // PDF Export Functions
  const exportCompanyReportToPDF = async () => {
    if (!companyReportRef.current) return;
    
    setExportingPDF(true);
    try {
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 25;
      const footerHeight = 10;
      const contentAreaHeight = pageHeight - headerHeight - footerHeight;
      const imgWidth = pageWidth - (2 * margin);
      
      // Load logo
      const logoData = await loadLogoForPDF();
      
      // Helper function to add page header
      const addPageHeader = () => {
        if (logoData) {
          pdf.addImage(logoData, 'PNG', margin, 5, 28, 7);
        }
        pdf.setFontSize(12);
        pdf.setTextColor(11, 106, 125);
        const title = language === 'es' ? 'Reporte de Empresa' : 'Company Report';
        pdf.text(title, margin + 32, 10);
        if (companyReport) {
          pdf.setFontSize(9);
          pdf.setTextColor(100, 100, 100);
          pdf.text(companyReport.company.nombre, margin + 32, 15);
        }
        pdf.setDrawColor(26, 135, 141);
        pdf.setLineWidth(0.5);
        pdf.line(margin, 18, pageWidth - margin, 18);
        pdf.setTextColor(0, 0, 0);
      };
      
      // Helper function to add page footer
      const addPageFooter = (pageNum, total) => {
        pdf.setDrawColor(26, 135, 141);
        pdf.setLineWidth(0.3);
        pdf.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        const dateStr = new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US');
        const pageText = language === 'es' ? `Página ${pageNum} de ${total}` : `Page ${pageNum} of ${total}`;
        pdf.text(`MEVIA - Data With Soul`, margin, pageHeight - 5);
        pdf.text(dateStr, pageWidth / 2 - 10, pageHeight - 5);
        pdf.text(pageText, pageWidth - margin - 22, pageHeight - 5);
        pdf.setTextColor(0, 0, 0);
      };
      
      // Capture the entire report as a single high-quality image
      const canvas = await html2canvas(companyReportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: companyReportRef.current.scrollWidth,
        windowHeight: companyReportRef.current.scrollHeight
      });
      
      // Calculate dimensions
      const pxPerMm = canvas.width / imgWidth;
      const contentAreaPx = contentAreaHeight * pxPerMm;
      const totalPages = Math.ceil(canvas.height / contentAreaPx);
      
      // Generate each page
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }
        
        addPageHeader();
        
        // Calculate the portion of the canvas to show on this page
        const srcY = Math.floor(page * contentAreaPx);
        const remainingHeight = canvas.height - srcY;
        const srcHeight = Math.min(Math.ceil(contentAreaPx), remainingHeight);
        
        if (srcHeight <= 0) continue;
        
        // Create a temporary canvas for this page's content
        const pageCanvas = document.createElement('canvas');
        const pageContext = pageCanvas.getContext('2d');
        pageCanvas.width = canvas.width;
        pageCanvas.height = srcHeight;
        
        // Fill with white background
        pageContext.fillStyle = '#ffffff';
        pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        
        // Draw the portion of the original canvas
        pageContext.drawImage(
          canvas,
          0, srcY,
          canvas.width, srcHeight,
          0, 0,
          canvas.width, srcHeight
        );
        
        const pageImgData = pageCanvas.toDataURL('image/png');
        const pageImgHeight = srcHeight / pxPerMm;
        
        // Add this page's image
        pdf.addImage(pageImgData, 'PNG', margin, headerHeight, imgWidth, pageImgHeight);
        
        addPageFooter(page + 1, totalPages);
      }
      
      // Save PDF
      const fileName = `company_report_${new Date().getTime()}.pdf`;
      pdf.save(fileName);
      
      toast.success(language === 'es' ? 'PDF exportado exitosamente' : 'PDF exported successfully');
    } catch (error) {
      logger.error('Error exporting PDF:', error);
      toast.error(language === 'es' ? 'Error al exportar PDF' : 'Error exporting PDF');
    } finally {
      setExportingPDF(false);
    }
  };

  const exportEmployeeReportToPDF = async () => {
    if (!employeeReportRef.current) return;
    
    setExportingPDF(true);
    try {
      // Hide elements that shouldn't appear in PDF
      const hiddenElements = employeeReportRef.current.querySelectorAll('.hide-on-pdf');
      hiddenElements.forEach(el => el.style.display = 'none');
      
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const headerHeight = 45;
      const footerHeight = 12;
      const contentAreaHeight = pageHeight - headerHeight - footerHeight;
      
      // Load logo
      const logoData = await loadLogoForPDF();
      
      // Find all sections to export separately for better page breaks
      const sections = employeeReportRef.current.querySelectorAll('.pdf-section, [class*="Card"]');
      
      // Capture each section and create optimized pages
      let currentY = headerHeight;
      let pageNum = 1;
      
      const addPageHeader = (pageNumber, totalPages) => {
        if (logoData) {
          // Logo proportions: 4:1 ratio for MEVIA horizontal logo (1600x400)
          pdf.addImage(logoData, 'PNG', margin, 6, 40, 10);
        }
        pdf.setDrawColor(26, 135, 141);
        pdf.setLineWidth(0.5);
        pdf.line(margin, 19, pageWidth - margin, 19);
        
        pdf.setFontSize(12);
        pdf.setTextColor(11, 106, 125);
        pdf.text(language === 'es' ? 'Reporte de Empleado' : 'Employee Report', margin + 45, 13);
        
        // Employee name
        if (selectedEmployee) {
          const employeeData = companyEmployees.find(emp => emp.id === selectedEmployee);
          const employeeName = individualRadarData?.assessment_info?.usuario_nombre || 
                              (employeeData ? `${employeeData.nombre || ''} ${employeeData.apellido || ''}`.trim() : '');
          pdf.setFontSize(9);
          pdf.setTextColor(100, 100, 100);
          if (employeeName) {
            pdf.text(employeeName, margin, 28);
          }
          if (employeeData?.email) {
            pdf.text(employeeData.email, margin, 34);
          }
          if (companyReport?.company?.nombre) {
            pdf.text(companyReport.company.nombre, margin, 40);
          }
        }
        pdf.setTextColor(0, 0, 0);
      };
      
      const addPageFooter = (pageNumber, totalPages) => {
        pdf.setDrawColor(26, 135, 141);
        pdf.setLineWidth(0.3);
        pdf.line(margin, pageHeight - 12, pageWidth - margin, pageHeight - 12);
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        pdf.text(
          `MEVIA - Data With Soul | ${new Date().toLocaleDateString()} | ${language === 'es' ? 'Pág.' : 'Page'} ${pageNumber}`,
          margin,
          pageHeight - 7
        );
        pdf.setTextColor(0, 0, 0);
      };
      
      // Capture entire report with high quality
      const canvas = await html2canvas(employeeReportRef.current, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
        windowWidth: employeeReportRef.current.scrollWidth,
        windowHeight: employeeReportRef.current.scrollHeight,
        imageTimeout: 15000
      });
      
      const imgWidth = pageWidth - (2 * margin);
      
      // Calculate ratio between canvas pixels and PDF mm
      const pxPerMm = canvas.width / imgWidth;
      const contentAreaPx = contentAreaHeight * pxPerMm;
      
      // Calculate how many pages we need
      const totalPages = Math.ceil(canvas.height / contentAreaPx);
      
      // Add pages
      for (let page = 0; page < totalPages; page++) {
        if (page > 0) {
          pdf.addPage();
        }
        
        addPageHeader(page + 1, totalPages);
        
        // Calculate the portion of the canvas for this page
        const srcY = Math.floor(page * contentAreaPx);
        const remainingHeight = canvas.height - srcY;
        const srcHeight = Math.min(Math.ceil(contentAreaPx), remainingHeight);
        
        // Skip if no content left
        if (srcHeight <= 0) continue;
        
        // Create temporary canvas for this page
        const pageCanvas = document.createElement('canvas');
        const pageContext = pageCanvas.getContext('2d');
        pageCanvas.width = canvas.width;
        pageCanvas.height = srcHeight;
        
        pageContext.fillStyle = '#ffffff';
        pageContext.fillRect(0, 0, pageCanvas.width, pageCanvas.height);
        
        pageContext.drawImage(
          canvas,
          0, srcY,
          canvas.width, srcHeight,
          0, 0,
          canvas.width, srcHeight
        );
        
        const pageImgData = pageCanvas.toDataURL('image/png');
        const pageImgHeight = srcHeight / pxPerMm;
        
        pdf.addImage(pageImgData, 'PNG', margin, headerHeight, imgWidth, pageImgHeight);
        addPageFooter(page + 1, totalPages);
      }
      
      // Save PDF
      const fileName = `employee_report_${new Date().getTime()}.pdf`;
      pdf.save(fileName);
      
      toast.success(language === 'es' ? 'PDF exportado exitosamente' : 'PDF exported successfully');
    } catch (error) {
      logger.error('Error exporting PDF:', error);
      toast.error(language === 'es' ? 'Error al exportar PDF' : 'Error exporting PDF');
    } finally {
      // Restore hidden elements
      const hiddenElements = employeeReportRef.current?.querySelectorAll('.hide-on-pdf');
      hiddenElements?.forEach(el => el.style.display = '');
      setExportingPDF(false);
    }
  };

  const exportCandidateReportToPDF = async () => {
    if (!candidateReportRef.current) return;
    
    setExportingPDF(true);
    try {
      // Hide elements that shouldn't appear in PDF
      const hiddenElements = candidateReportRef.current.querySelectorAll('.hide-on-pdf');
      hiddenElements.forEach(el => el.style.display = 'none');
      
      const pdf = new jsPDF('portrait', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 12;
      const headerHeight = 25;
      const footerHeight = 12;
      const contentAreaHeight = pageHeight - headerHeight - footerHeight;
      const imgWidth = pageWidth - (2 * margin);
      
      // Load logo
      const logoData = await loadLogoForPDF();
      
      // Get candidate info
      const candidateData = candidates.find(cand => cand.id === selectedCandidate);
      const candidateName = candidateRadarData?.assessment_info?.usuario_nombre || 
                          candidateData?.name || 'Candidate';
      
      // Find all Card elements (sections) within the report
      const sections = candidateReportRef.current.querySelectorAll(':scope > div.pdf-section, :scope > div[class*="rounded"]');
      
      // If no sections found, fall back to capturing all direct children
      let elementsToCapture = sections.length > 0 ? Array.from(sections) : 
        Array.from(candidateReportRef.current.children).filter(el => 
          el.tagName !== 'SCRIPT' && 
          el.style.display !== 'none' && 
          !el.classList.contains('hide-on-pdf')
        );
      
      let currentY = headerHeight;
      let pageNum = 1;
      let totalPages = 1; // Will be updated at the end
      
      // Helper to add header
      const addHeader = () => {
        if (logoData) {
          pdf.addImage(logoData, 'PNG', margin, 5, 28, 7);
        }
        pdf.setFontSize(11);
        pdf.setTextColor(11, 106, 125);
        pdf.text(language === 'es' ? 'Reporte de Candidato' : 'Candidate Report', margin + 32, 9);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`${candidateName} | ${candidateData?.email || ''}`, margin + 32, 14);
        pdf.setDrawColor(26, 135, 141);
        pdf.setLineWidth(0.4);
        pdf.line(margin, 18, pageWidth - margin, 18);
        pdf.setTextColor(0, 0, 0);
      };
      
      // Helper to add footer
      const addFooter = (pageNumber) => {
        pdf.setDrawColor(26, 135, 141);
        pdf.setLineWidth(0.3);
        pdf.line(margin, pageHeight - 10, pageWidth - margin, pageHeight - 10);
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        const dateStr = new Date().toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US');
        pdf.text(`MEVIA - Data With Soul | ${dateStr}`, margin, pageHeight - 6);
        // Page number will be updated at the end
        pdf.setTextColor(0, 0, 0);
      };
      
      // Add first page header
      addHeader();
      
      // Process each section
      for (let i = 0; i < elementsToCapture.length; i++) {
        const element = elementsToCapture[i];
        
        // Skip hidden elements or selection cards
        if (element.style.display === 'none' || 
            element.querySelector('[class*="select_candidate"]')) {
          continue;
        }
        
        // Capture the section
        const canvas = await html2canvas(element, {
          scale: 2,
          useCORS: true,
          logging: false,
          backgroundColor: '#ffffff',
          windowWidth: element.scrollWidth,
          imageTimeout: 10000
        });
        
        const sectionImgHeight = (canvas.height * imgWidth) / canvas.width;
        
        // Check if this section fits on the current page
        if (currentY + sectionImgHeight > pageHeight - footerHeight) {
          // Section doesn't fit - check if it's too tall for any single page
          if (sectionImgHeight > contentAreaHeight) {
            // Section is taller than a page - need to split it
            const pxPerMm = canvas.width / imgWidth;
            const contentAreaPx = contentAreaHeight * pxPerMm;
            const sectionPages = Math.ceil(canvas.height / contentAreaPx);
            
            for (let sp = 0; sp < sectionPages; sp++) {
              if (sp > 0 || currentY > headerHeight + 5) {
                // Add footer to current page before new page
                addFooter(pageNum);
                pdf.addPage();
                pageNum++;
                addHeader();
                currentY = headerHeight;
              }
              
              const srcY = Math.floor(sp * contentAreaPx);
              const remainingHeight = canvas.height - srcY;
              const srcHeight = Math.min(Math.ceil(contentAreaPx), remainingHeight);
              
              if (srcHeight <= 0) continue;
              
              // Create temp canvas for this portion
              const tempCanvas = document.createElement('canvas');
              const tempCtx = tempCanvas.getContext('2d');
              tempCanvas.width = canvas.width;
              tempCanvas.height = srcHeight;
              
              tempCtx.fillStyle = '#ffffff';
              tempCtx.fillRect(0, 0, tempCanvas.width, tempCanvas.height);
              tempCtx.drawImage(canvas, 0, srcY, canvas.width, srcHeight, 0, 0, canvas.width, srcHeight);
              
              const portionImg = tempCanvas.toDataURL('image/png');
              const portionHeight = srcHeight / pxPerMm;
              
              pdf.addImage(portionImg, 'PNG', margin, currentY, imgWidth, portionHeight);
              currentY += portionHeight + 3;
            }
          } else {
            // Section fits on a page but not current page - start new page
            addFooter(pageNum);
            pdf.addPage();
            pageNum++;
            addHeader();
            currentY = headerHeight;
            
            const imgData = canvas.toDataURL('image/png');
            pdf.addImage(imgData, 'PNG', margin, currentY, imgWidth, sectionImgHeight);
            currentY += sectionImgHeight + 3;
          }
        } else {
          // Section fits on current page
          const imgData = canvas.toDataURL('image/png');
          pdf.addImage(imgData, 'PNG', margin, currentY, imgWidth, sectionImgHeight);
          currentY += sectionImgHeight + 3;
        }
      }
      
      // Add footer to last page
      addFooter(pageNum);
      totalPages = pageNum;
      
      // Update all page numbers
      for (let p = 1; p <= totalPages; p++) {
        pdf.setPage(p);
        pdf.setFontSize(7);
        pdf.setTextColor(100, 100, 100);
        const pageText = language === 'es' ? `Página ${p} de ${totalPages}` : `Page ${p} of ${totalPages}`;
        pdf.text(pageText, pageWidth - margin - 22, pageHeight - 6);
      }
      
      // Save PDF
      const fileName = `candidate_report_${new Date().getTime()}.pdf`;
      pdf.save(fileName);
      
      toast.success(language === 'es' ? 'PDF exportado exitosamente' : 'PDF exported successfully');
    } catch (error) {
      logger.error('Error exporting PDF:', error);
      toast.error(language === 'es' ? 'Error al exportar PDF' : 'Error exporting PDF');
    } finally {
      // Restore hidden elements
      const hiddenElements = candidateReportRef.current?.querySelectorAll('.hide-on-pdf');
      hiddenElements?.forEach(el => el.style.display = '');
      setExportingPDF(false);
    }
  };


  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin mx-auto" style={{ color: '#1A878D' }} />
          <p className="mt-4 text-gray-600">
            {language === 'es' ? 'Cargando reportes...' : 'Loading reports...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b" style={{ borderBottomColor: '#1A878D', borderBottomWidth: '2px' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-4">
              <img 
                src="/images/mevia-logo-horizontal.png" 
                alt="MEVIA" 
                className="h-10"
              />
              <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('buttons.back', 'Volver')}
              </Button>
              <div className="border-l pl-4" style={{ borderColor: '#E5E7EB' }}>
                <h1 className="text-xl font-bold text-gray-900">
                  {t('reports.title', 'Reportes y Análisis')}
                </h1>
                <p className="text-sm text-gray-600">
                  {t('reports.description', 'Análisis detallado de evaluaciones con gráficos circulares')}
                </p>
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {/* Language Selector */}
              <Button
                onClick={() => changeLanguage(language === 'es' ? 'en' : 'es')}
                variant="outline"
                size="sm"
                className="flex items-center space-x-2"
              >
                <Globe className="h-4 w-4" />
                <span className="font-semibold">{language === 'es' ? '🇪🇸 ES' : '🇺🇸 EN'}</span>
              </Button>
              
              <Button onClick={() => navigate('/employees')} variant="outline">
                <Users className="h-4 w-4 mr-2" />
                {t('buttons.manage_employees', 'Gestionar Empleados')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <Tabs defaultValue={employeeId ? "individual" : "company"} className="space-y-6">
          <TabsList className="grid grid-cols-4 w-full max-w-3xl">
            <TabsTrigger value="company">
              <Building className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Empresa' : 'Company'}
            </TabsTrigger>
            <TabsTrigger value="individual">
              <Users className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Empleados' : 'Employees'}
            </TabsTrigger>
            <TabsTrigger value="candidates">
              <UserPlus className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Candidatos' : 'Candidates'}
            </TabsTrigger>
            <TabsTrigger value="advanced">
              <Target className="h-4 w-4 mr-2" />
              {language === 'es' ? 'Avanzado' : 'Advanced'}
            </TabsTrigger>
          </TabsList>
          
          {/* Company Report */}
          <TabsContent value="company">
            <div className="space-y-6">
              {/* Export PDF Button */}
              <div className="flex justify-end">
                {canExportPDF ? (
                  <Button
                    onClick={exportCompanyReportToPDF}
                    disabled={exportingPDF || !companyReport}
                    variant="outline"
                    size="sm"
                    data-testid="export-company-pdf-btn"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    {exportingPDF ? (language === 'es' ? 'Exportando...' : 'Exporting...') : (language === 'es' ? 'Exportar PDF' : 'Export PDF')}
                  </Button>
                ) : (
                  <Button variant="outline" size="sm" disabled className="opacity-50">
                    <Lock className="h-4 w-4 mr-2" />
                    {language === 'es' ? 'PDF (Plan Superior)' : 'PDF (Upgrade Plan)'}
                  </Button>
                )}
              </div>

              {/* Content to export */}
              <div ref={companyReportRef}>
                
                {/* 1. Company HR Indicators and Risk Counters */}
                {companyHRIndicators && companyHRIndicators.summary && (
                  <div className="pdf-section" style={{ pageBreakInside: 'avoid' }}>
                  <Card className="mb-6">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        <Activity className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
                        {language === 'es' ? 'Indicadores de RH de la Empresa' : 'Company HR Indicators'}
                      </CardTitle>
                      <CardDescription>
                        {language === 'es' 
                          ? `Promedios generales basados en ${companyHRIndicators.total_employees || companyHRIndicators.assessed_count} empleados evaluados`
                          : `General averages based on ${companyHRIndicators.total_employees || companyHRIndicators.assessed_count} evaluated employees`}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      {/* IPS Summary - Company Aggregate - 3 Columns Layout */}
                      {companyHRIndicators.summary.ips && (
                        <div className="mb-6 p-4 bg-white rounded-xl border" style={{ borderLeftWidth: '4px', borderLeftColor: companyHRIndicators.summary.ips.color }}>
                          <div className="grid grid-cols-3 gap-4 items-center">
                            {/* Column 1: Score */}
                            <div className="text-center py-2">
                              <p className="text-base font-bold text-gray-700 uppercase tracking-wide">
                                SCORE MEVIA
                              </p>
                              <p className="text-sm text-gray-500 mb-1">
                                {language === 'es' ? 'Permanencia Saludable' : 'Healthy Retention'}
                              </p>
                              <span 
                                className="text-5xl font-bold block"
                                style={{ color: companyHRIndicators.summary.ips.color }}
                              >
                                {companyHRIndicators.summary.ips.score?.toFixed(1) || 'N/A'}
                              </span>
                              <span 
                                className="inline-block text-sm font-semibold px-3 py-1 rounded mt-2"
                                style={{ 
                                  backgroundColor: `${companyHRIndicators.summary.ips.color}20`,
                                  color: companyHRIndicators.summary.ips.color
                                }}
                              >
                                {companyHRIndicators.summary.ips.level?.replace(/_/g, ' ')}
                              </span>
                            </div>
                            
                            {/* Column 2: Distribution */}
                            <div className="border-x border-gray-100 px-4 py-1">
                              <div className="space-y-1.5">
                                <div className="flex items-center gap-2">
                                  <span className="w-3 h-3 rounded-full flex-shrink-0 bg-green-500" />
                                  <span className="text-sm text-gray-600 flex-1">{language === 'es' ? 'Saludable' : 'Healthy'}</span>
                                  <span className="text-lg font-bold text-gray-800">{companyHRIndicators.summary.ips.distribution?.SALUDABLE || 0}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="w-3 h-3 rounded-full flex-shrink-0 bg-amber-500" />
                                  <span className="text-sm text-gray-600 flex-1">{language === 'es' ? 'Moderado' : 'Moderate'}</span>
                                  <span className="text-lg font-bold text-gray-800">{companyHRIndicators.summary.ips.distribution?.MODERADO || 0}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="w-3 h-3 rounded-full flex-shrink-0 bg-orange-500" />
                                  <span className="text-sm text-gray-600 flex-1">{language === 'es' ? 'Alto Riesgo' : 'High Risk'}</span>
                                  <span className="text-lg font-bold text-gray-800">{companyHRIndicators.summary.ips.distribution?.ALTO_RIESGO || 0}</span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <span className="w-3 h-3 rounded-full flex-shrink-0 bg-red-500" />
                                  <span className="text-sm text-gray-600 flex-1">{language === 'es' ? 'Crítico' : 'Critical'}</span>
                                  <span className="text-lg font-bold text-gray-800">{companyHRIndicators.summary.ips.distribution?.CRITICO || 0}</span>
                                </div>
                              </div>
                            </div>
                            
                            {/* Column 3: Donut Chart */}
                            <div className="flex items-center justify-center">
                              <svg width="140" height="140" viewBox="0 0 140 140">
                                <circle cx="70" cy="70" r="50" fill="none" stroke="#E5E7EB" strokeWidth="18" />
                                {(() => {
                                  const dist = companyHRIndicators.summary.ips.distribution || {};
                                  const total = (dist.SALUDABLE || 0) + (dist.MODERADO || 0) + (dist.ALTO_RIESGO || 0) + (dist.CRITICO || 0);
                                  if (total === 0) return null;
                                  
                                  const levels = [
                                    { key: 'SALUDABLE', color: '#22C55E' },
                                    { key: 'MODERADO', color: '#F59E0B' },
                                    { key: 'ALTO_RIESGO', color: '#F97316' },
                                    { key: 'CRITICO', color: '#EF4444' }
                                  ];
                                  
                                  let currentAngle = -90;
                                  return levels.map(({ key, color }) => {
                                    const count = dist[key] || 0;
                                    if (count === 0) return null;
                                    const angle = (count / total) * 360;
                                    const startAngle = currentAngle;
                                    const endAngle = currentAngle + angle;
                                    currentAngle = endAngle;
                                    
                                    const startRad = (startAngle * Math.PI) / 180;
                                    const endRad = (endAngle * Math.PI) / 180;
                                    const x1 = 70 + 50 * Math.cos(startRad);
                                    const y1 = 70 + 50 * Math.sin(startRad);
                                    const x2 = 70 + 50 * Math.cos(endRad);
                                    const y2 = 70 + 50 * Math.sin(endRad);
                                    const largeArc = angle > 180 ? 1 : 0;
                                    
                                    return (
                                      <path
                                        key={key}
                                        d={`M ${x1} ${y1} A 50 50 0 ${largeArc} 1 ${x2} ${y2}`}
                                        fill="none"
                                        stroke={color}
                                        strokeWidth="18"
                                        strokeLinecap="round"
                                      />
                                    );
                                  });
                                })()}
                                <text x="70" y="75" textAnchor="middle" fontSize="28" fontWeight="bold" fill="#374151">
                                  {(companyHRIndicators.summary.ips.distribution?.SALUDABLE || 0) + 
                                   (companyHRIndicators.summary.ips.distribution?.MODERADO || 0) + 
                                   (companyHRIndicators.summary.ips.distribution?.ALTO_RIESGO || 0) + 
                                   (companyHRIndicators.summary.ips.distribution?.CRITICO || 0)}
                                </text>
                                <text x="70" y="92" textAnchor="middle" fontSize="10" fill="#6B7280">
                                  {language === 'es' ? 'empleados' : 'employees'}
                                </text>
                              </svg>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Risk Counters */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
                        <div className="bg-red-50 p-3 rounded-lg border-t-4 border-red-500">
                          <p className="text-xs font-medium text-red-700">{language === 'es' ? 'Alto Riesgo' : 'High Risk'}</p>
                          <p className="text-2xl font-bold text-red-600">{companyHRIndicators.summary.risk_distribution?.high || 0}</p>
                        </div>
                        <div className="bg-yellow-50 p-3 rounded-lg border-t-4 border-yellow-500">
                          <p className="text-xs font-medium text-yellow-700">{language === 'es' ? 'Riesgo Medio' : 'Medium Risk'}</p>
                          <p className="text-2xl font-bold text-yellow-600">{companyHRIndicators.summary.risk_distribution?.medium || 0}</p>
                        </div>
                        <div className="bg-green-50 p-3 rounded-lg border-t-4 border-green-500">
                          <p className="text-xs font-medium text-green-700">{language === 'es' ? 'Bajo Riesgo' : 'Low Risk'}</p>
                          <p className="text-2xl font-bold text-green-600">{companyHRIndicators.summary.risk_distribution?.low || 0}</p>
                        </div>
                        <div className="bg-purple-50 p-3 rounded-lg border-t-4 border-purple-500">
                          <p className="text-xs font-medium text-purple-700">{language === 'es' ? 'Alertas Totales' : 'Total Alerts'}</p>
                          <p className="text-2xl font-bold text-purple-600">{companyHRIndicators.summary.total_red_flags || 0}</p>
                        </div>
                      </div>
                      
                      {/* HR Indicators Averages */}
                      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
                        {Object.entries(companyHRIndicators.summary.averages || {}).map(([key, value]) => {
                          const indicatorNames = {
                            irr: { es: 'Riesgo Rotación', en: 'Turnover Risk' },
                            ico: { es: 'Compromiso', en: 'Commitment' },
                            iap: { es: 'Adecuación', en: 'Job Fit' },
                            ipc: { es: 'Potencial', en: 'Potential' },
                            icf: { es: 'Cultural Fit', en: 'Cultural Fit' },
                            ipe: { es: 'Productividad', en: 'Productivity' },
                            ilo: { es: 'Lealtad', en: 'Loyalty' }
                          };
                          const getIndicatorColor = (k, v) => {
                            if (k === 'irr') return v >= 60 ? 'text-red-600' : v >= 40 ? 'text-yellow-600' : 'text-green-600';
                            return v >= 70 ? 'text-green-600' : v >= 50 ? 'text-yellow-600' : 'text-red-600';
                          };
                          return (
                            <div key={key} className="bg-white p-2 rounded border border-gray-200 text-center">
                              <p className="text-xs font-medium text-gray-500 uppercase">{key}</p>
                              <p className={`text-lg font-bold ${getIndicatorColor(key, value)}`}>{value}%</p>
                              <p className="text-xs text-gray-400">{indicatorNames[key]?.[language] || key}</p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                  </div>
                )}

                {/* 2. Company Stats - Assessments and Departments (compact) */}
                {companyReport && (
                  <div className="pdf-section" style={{ pageBreakInside: 'avoid' }}>
                  <Card className="mb-4">
                    <CardContent className="py-3">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Building className="h-5 w-5" style={{ color: '#1A878D' }} />
                          <span className="font-semibold">{companyReport.company.nombre}</span>
                          <span className="text-gray-400">|</span>
                          <span className="text-sm text-gray-500">{companyReport.company.industria}</span>
                        </div>
                        <div className="flex items-center gap-6 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{language === 'es' ? 'Evaluaciones:' : 'Assessments:'}</span>
                            <span className="font-bold text-green-600">{companyReport.analytics.total_completed}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{language === 'es' ? 'Departamentos:' : 'Departments:'}</span>
                            <span className="font-bold text-purple-600">{Object.keys(companyReport.analytics.departments).length}</span>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </div>
                )}

                {/* 3. Profile Charts - Company Profile (left) and Employees Average (right) */}
                <div className="pdf-section" style={{ pageBreakInside: 'avoid', pageBreakBefore: 'auto' }}>
                <Card className="mb-4">
                  <CardHeader className="pb-1">
                    <CardTitle className="flex items-center text-base">
                      <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
                      {language === 'es' ? 'Perfiles de Dimensiones' : 'Dimension Profiles'}
                    </CardTitle>
                    <CardDescription className="text-xs">
                      {language === 'es' 
                        ? 'Comparación de los perfiles de la empresa y el promedio de empleados'
                        : 'Comparison of company and employee average profiles'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {/* Scores Row - Compatibility, Balance, Growth */}
                    {companyReport && (
                      <div className="grid grid-cols-3 gap-3 mb-3">
                        <div className="text-center p-2 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-xs font-medium text-blue-700">{language === 'es' ? 'Compatibilidad' : 'Compatibility'}</p>
                          <p className={`text-xl font-bold ${getScoreColor(companyReport.analytics.avg_compatibility)}`}>
                            {formatScore(companyReport.analytics.avg_compatibility)}%
                          </p>
                        </div>
                        <div className="text-center p-2 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-xs font-medium text-green-700">{language === 'es' ? 'Balance' : 'Balance'}</p>
                          <p className="text-xl font-bold text-green-600">
                            {companyProfileData?.scores?.balance ? Math.round(companyProfileData.scores.balance) : '--'}%
                          </p>
                        </div>
                        <div className="text-center p-2 bg-purple-50 rounded-lg border border-purple-100">
                          <p className="text-xs font-medium text-purple-700">{language === 'es' ? 'Crecimiento' : 'Growth'}</p>
                          <p className="text-xl font-bold text-purple-600">
                            {companyProfileData?.scores?.growth ? Math.round(companyProfileData.scores.growth) : '--'}%
                          </p>
                        </div>
                      </div>
                    )}
                    {/* Charts Row */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {/* Company Profile Chart */}
                      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="p-2 border-b border-gray-100 bg-gray-50">
                          <h4 className="text-xs font-semibold text-center text-gray-700">
                            {language === 'es' ? 'Perfil de la Empresa' : 'Company Profile'}
                          </h4>
                        </div>
                        <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                          {companyProfileData && companyProfileData.chart_data && companyProfileData.chart_data.ofertas && !companyProfileData.chart_data.ofertas.every(val => val === 0) ? (
                            <CompanyRadarChart 
                              key={`company-profile-${language}`}
                              companyData={{
                                radar_chart: {
                                  labels: language === 'es' 
                                    ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                    : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                  datasets: [
                                    {
                                      label: language === 'es' ? 'Ofertas' : 'Offers',
                                      data: companyProfileData.chart_data.ofertas,
                                      backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                      borderColor: 'rgba(34, 197, 94, 1)',
                                      borderWidth: 2
                                    },
                                    {
                                      label: language === 'es' ? 'Necesidades' : 'Needs',
                                      data: companyProfileData.chart_data.requerimientos,
                                      backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                      borderColor: 'rgba(239, 68, 68, 1)',
                                      borderWidth: 2
                                    }
                                  ]
                                }
                              }}
                              className="w-full"
                            />
                          ) : (
                            <div className="text-center py-8">
                              <Building className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                              <p className="text-gray-500 text-sm mb-2">
                                {language === 'es' ? 'Evaluación de empresa no completada' : 'Company assessment not completed'}
                              </p>
                              <Button onClick={() => navigate('/assessment')} variant="outline" size="sm">
                                {language === 'es' ? 'Completar Evaluación' : 'Complete Assessment'}
                              </Button>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Employee Average Chart */}
                      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="p-2 border-b border-gray-100 bg-gray-50">
                          <h4 className="text-xs font-semibold text-center text-gray-700">
                            {language === 'es' ? 'Promedio de Empleados' : 'Employees Average'}
                          </h4>
                        </div>
                        <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                          {companyRadarData ? (
                            <CompanyRadarChart 
                              key={`employee-average-${language}`}
                              companyData={{
                                radar_chart: {
                                  labels: language === 'es' 
                                    ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                    : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                  datasets: companyRadarData.radar_chart?.datasets || []
                                }
                              }}
                              className="w-full"
                            />
                          ) : (
                            <div className="text-center py-8">
                              <Users className="h-12 w-12 text-gray-300 mx-auto mb-2" />
                              <p className="text-gray-500 text-sm">
                                {language === 'es' ? 'No hay suficientes datos de empleados' : 'Not enough employee data'}
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
                </div>

                {/* 4. Comparison Charts - Company Offer vs Employee Need | Employee Offer vs Company Need */}
                {/* NUEVA PÁGINA - Análisis de Compatibilidad */}
                {companyRadarData && companyProfileData && companyProfileData.chart_data && (
                  <div className="pdf-section" style={{ pageBreakBefore: 'always' }}>
                  <Card className="mb-6">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        <BarChart3 className="h-5 w-5 mr-2 text-purple-600" />
                        {language === 'es' ? 'Análisis de Compatibilidad' : 'Compatibility Analysis'}
                      </CardTitle>
                      <CardDescription>
                        {language === 'es' 
                          ? 'Comparación de ofertas y necesidades entre empresa y empleados'
                          : 'Comparison of offers and needs between company and employees'}
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {/* Company Offer vs Employee Needs */}
                        <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                          <div className="p-2 border-b border-gray-100 bg-gray-50">
                            <h4 className="text-xs font-semibold text-center text-gray-700">
                              {language === 'es' ? 'Oferta Empresa vs Necesidad Empleados' : 'Company Offer vs Employee Needs'}
                            </h4>
                          </div>
                          <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                            {(() => {
                              const data = getComparisonData();
                              return data ? (
                                <CompanyRadarChart 
                                  key={`company-offer-emp-need-${language}`}
                                  companyData={{
                                    radar_chart: {
                                      labels: language === 'es' 
                                        ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                        : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                      datasets: [
                                        {
                                          label: language === 'es' ? 'Oferta Empresa' : 'Company Offer',
                                          data: companyProfileData.chart_data.ofertas,
                                          backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                          borderColor: 'rgba(34, 197, 94, 1)',
                                          borderWidth: 2
                                        },
                                        {
                                          label: language === 'es' ? 'Necesidad Empleados' : 'Employee Needs',
                                          data: data.employees_average.requerimientos,
                                          backgroundColor: 'rgba(168, 85, 247, 0.2)',
                                          borderColor: 'rgba(168, 85, 247, 1)',
                                          borderWidth: 2
                                        }
                                      ]
                                    }
                                  }}
                                  className="w-full"
                                />
                              ) : (
                                <div className="text-gray-400 text-sm text-center">
                                  {language === 'es' ? 'Cargando...' : 'Loading...'}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                        
                        {/* Employee Offer vs Company Needs */}
                        <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                          <div className="p-2 border-b border-gray-100 bg-gray-50">
                            <h4 className="text-xs font-semibold text-center text-gray-700">
                              {language === 'es' ? 'Oferta Empleados vs Necesidad Empresa' : 'Employee Offer vs Company Needs'}
                            </h4>
                          </div>
                          <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                            {(() => {
                              const data = getComparisonData();
                              return data ? (
                                <CompanyRadarChart 
                                  key={`emp-offer-company-need-${language}`}
                                  companyData={{
                                    radar_chart: {
                                      labels: language === 'es' 
                                        ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                        : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                      datasets: [
                                        {
                                          label: language === 'es' ? 'Oferta Empleados' : 'Employee Offer',
                                          data: data.employees_average.ofertas,
                                          backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                          borderColor: 'rgba(59, 130, 246, 1)',
                                          borderWidth: 2
                                        },
                                        {
                                          label: language === 'es' ? 'Necesidad Empresa' : 'Company Needs',
                                          data: companyProfileData.chart_data.requerimientos,
                                          backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                          borderColor: 'rgba(239, 68, 68, 1)',
                                          borderWidth: 2
                                        }
                                      ]
                                    }
                                  }}
                                  className="w-full"
                                />
                              ) : (
                                <div className="text-gray-400 text-sm text-center">
                                  {language === 'es' ? 'Cargando...' : 'Loading...'}
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  </div>
                )}

                {/* 5. Compact Interpretations */}
                {companyProfileData && companyProfileData.interpretation && (
                  <div className="pdf-section" style={{ pageBreakInside: 'avoid' }}>
                  <Card className="mb-6">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        📋 {language === 'es' ? 'Análisis de Perfiles' : 'Profile Analysis'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="text-sm text-gray-700">
                        <ReportInterpretation 
                          interpretation={companyProfileData.interpretation} 
                          userType="company" 
                          compact={true}
                        />
                      </div>
                    </CardContent>
                  </Card>
                  </div>
                )}

                {/* 6. General Conclusions */}
                {(companyHRIndicators || companyProfileData) && (
                  <div className="pdf-section" style={{ pageBreakInside: 'avoid', pageBreakBefore: 'auto' }}>
                  <Card className="mb-6">
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        📊 {language === 'es' ? 'Conclusiones Generales' : 'General Conclusions'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-4">
                      {/* HR Indicators Conclusion */}
                      {companyHRIndicators && companyHRIndicators.summary && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h4 className="font-semibold text-sm text-gray-800 mb-2">
                            {language === 'es' ? 'Indicadores de Recursos Humanos' : 'HR Indicators'}
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {(() => {
                              const avg = companyHRIndicators.summary.averages;
                              const risk = companyHRIndicators.summary.risk_distribution;
                              const total = companyHRIndicators.total_employees;
                              
                              if (language === 'es') {
                                let conclusion = `De ${total} empleados evaluados, `;
                                if (risk.high > 0) {
                                  conclusion += `${risk.high} presentan alto riesgo de rotación y requieren atención inmediata. `;
                                }
                                if (avg.ico >= 60) {
                                  conclusion += 'El compromiso organizacional promedio es positivo. ';
                                } else {
                                  conclusion += 'El compromiso organizacional presenta oportunidades de mejora. ';
                                }
                                if (avg.icf >= 70) {
                                  conclusion += 'La compatibilidad cultural general es alta.';
                                } else if (avg.icf < 50) {
                                  conclusion += 'Se recomienda fortalecer la integración cultural.';
                                }
                                return conclusion;
                              } else {
                                let conclusion = `Of ${total} evaluated employees, `;
                                if (risk.high > 0) {
                                  conclusion += `${risk.high} present high turnover risk and require immediate attention. `;
                                }
                                if (avg.ico >= 60) {
                                  conclusion += 'Average organizational commitment is positive. ';
                                } else {
                                  conclusion += 'Organizational commitment presents improvement opportunities. ';
                                }
                                if (avg.icf >= 70) {
                                  conclusion += 'Overall cultural compatibility is high.';
                                } else if (avg.icf < 50) {
                                  conclusion += 'Strengthening cultural integration is recommended.';
                                }
                                return conclusion;
                              }
                            })()}
                          </p>
                        </div>
                      )}
                      
                      {/* Profile Conclusion */}
                      {companyReport && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h4 className="font-semibold text-sm text-gray-800 mb-2">
                            {language === 'es' ? 'Perfil de Dimensiones' : 'Dimensions Profile'}
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {(() => {
                              const compat = companyReport.analytics.avg_compatibility;
                              
                              if (language === 'es') {
                                let conclusion = `Con una compatibilidad promedio del ${formatScore(compat)}%, `;
                                if (compat >= 80) {
                                  conclusion += 'existe excelente alineación entre lo que los empleados ofrecen y lo que la empresa necesita. ';
                                } else if (compat >= 60) {
                                  conclusion += 'hay buena alineación con áreas identificadas para desarrollo. ';
                                } else {
                                  conclusion += 'se recomienda analizar las brechas entre perfiles y desarrollar estrategias de mejora. ';
                                }
                                conclusion += 'El análisis de los 10 capitales permite identificar fortalezas y áreas de oportunidad en la organización.';
                                return conclusion;
                              } else {
                                let conclusion = `With ${formatScore(compat)}% average compatibility, `;
                                if (compat >= 80) {
                                  conclusion += 'there is excellent alignment between what employees offer and what the company needs. ';
                                } else if (compat >= 60) {
                                  conclusion += 'there is good alignment with identified areas for development. ';
                                } else {
                                  conclusion += 'analyzing profile gaps and developing improvement strategies is recommended. ';
                                }
                                conclusion += 'The 10 capitals analysis allows identifying organizational strengths and opportunities.';
                                return conclusion;
                              }
                            })()}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  </div>
                )}

                {/* 7. Indicators Guide */}
                <div className="pdf-section" style={{ pageBreakInside: 'avoid', pageBreakBefore: 'auto' }}>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="flex items-center text-base">
                      ℹ️ {language === 'es' ? 'Guía de Indicadores' : 'Indicators Guide'}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-600">
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[32px]">IRR:</span>
                        <span>{language === 'es' 
                          ? 'Riesgo de Rotación. Mide el desajuste entre necesidades del empleado y oferta de la empresa.' 
                          : 'Turnover Risk. Measures mismatch between employee needs and company offer.'}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[32px]">ICO:</span>
                        <span>{language === 'es' 
                          ? 'Compromiso Organizacional. Evalúa identificación y compromiso con la organización.' 
                          : 'Organizational Commitment. Evaluates identification and commitment to organization.'}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[32px]">IAP:</span>
                        <span>{language === 'es' 
                          ? 'Adecuación al Puesto. Mide coincidencia de fortalezas con requerimientos del puesto.' 
                          : 'Job Fit. Measures match between strengths and job requirements.'}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[32px]">IPC:</span>
                        <span>{language === 'es' 
                          ? 'Potencial de Crecimiento. Evalúa capacidad de desarrollo profesional.' 
                          : 'Growth Potential. Evaluates professional development capacity.'}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[32px]">ICF:</span>
                        <span>{language === 'es' 
                          ? 'Cultural Fit. Mide compatibilidad de valores con la cultura organizacional.' 
                          : 'Cultural Fit. Measures value compatibility with organizational culture.'}</span>
                      </div>
                      <div className="flex items-start gap-2">
                        <span className="font-semibold text-gray-700 min-w-[32px]">IPE:</span>
                        <span>{language === 'es' 
                          ? 'Productividad Esperada. Predice nivel de productividad basado en competencias.' 
                          : 'Expected Productivity. Predicts productivity based on competencies.'}</span>
                      </div>
                      <div className="flex items-start gap-2 md:col-span-2">
                        <span className="font-semibold text-gray-700 min-w-[32px]">ILO:</span>
                        <span>{language === 'es' 
                          ? 'Lealtad Organizacional. Evalúa vínculo emocional con la misión y valores de la empresa.' 
                          : 'Organizational Loyalty. Evaluates emotional bond with company mission and values.'}</span>
                      </div>
                    </div>
                    
                    {/* Scores Calculation Explanation */}
                    <div className="mt-4 pt-3 border-t border-gray-200">
                      <h5 className="text-xs font-semibold text-gray-700 mb-2">
                        {language === 'es' ? 'Cálculo de Scores de Compatibilidad' : 'Compatibility Scores Calculation'}
                      </h5>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600">
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-blue-600 min-w-[90px]">{language === 'es' ? 'Compatibilidad:' : 'Compatibility:'}</span>
                          <span>{language === 'es' 
                            ? 'Mide el ajuste general entre ofertas y necesidades de empresa y empleados.' 
                            : 'Measures overall fit between offers and needs of company and employees.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-green-600 min-w-[90px]">{language === 'es' ? 'Balance:' : 'Balance:'}</span>
                          <span>{language === 'es' 
                            ? 'Evalúa la distribución equilibrada de capitales en los perfiles.' 
                            : 'Evaluates balanced distribution of capitals in profiles.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-purple-600 min-w-[90px]">{language === 'es' ? 'Crecimiento:' : 'Growth:'}</span>
                          <span>{language === 'es' 
                            ? 'Indica potencial de desarrollo basado en brechas identificadas.' 
                            : 'Indicates development potential based on identified gaps.'}</span>
                        </div>
                      </div>
                    </div>
                    
                    <p className="text-xs text-gray-400 mt-3 italic">
                      {language === 'es' 
                        ? 'Los indicadores se calculan a partir del perfil de dimensiones (10 capitales) y representan promedios de todos los empleados evaluados.' 
                        : 'Indicators are calculated from dimension profiles (10 capitals) and represent averages of all evaluated employees.'}
                    </p>
                  </CardContent>
                </Card>
                </div>

              </div> {/* End of companyReportRef */}
            </div>
          </TabsContent>
          
          {/* Individual Report */}
          <TabsContent value="individual">
            <div className="space-y-6">
              {/* Export PDF Button - Only show when employee is selected */}
              {selectedEmployee && individualRadarData && (
                <div className="flex justify-end">
                  {canExportPDF ? (
                    <Button
                      onClick={exportEmployeeReportToPDF}
                      disabled={exportingPDF}
                      variant="outline"
                      size="sm"
                      data-testid="export-employee-pdf-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {exportingPDF ? (language === 'es' ? 'Exportando...' : 'Exporting...') : (language === 'es' ? 'Exportar PDF' : 'Export PDF')}
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" disabled className="opacity-50">
                      <Lock className="h-4 w-4 mr-2" />
                      {language === 'es' ? 'PDF (Plan Superior)' : 'PDF (Upgrade Plan)'}
                    </Button>
                  )}
                </div>
              )}

              {/* Content to export */}
              <div ref={selectedEmployee ? employeeReportRef : null}>
                {/* Employee Selection */}
                {!selectedEmployee && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('reports.select_employee', 'Select Employee')}</CardTitle>
                    <CardDescription>
                      {t('reports.select_employee_desc', 'Select an employee to view their assessment circular chart')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="grid gap-3">
                      {companyEmployees.filter(emp => emp.completed_assessments > 0).map((employee) => (
                        <div key={employee.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                          <div className="flex justify-between items-center">
                            <div className="flex items-center space-x-3">
                              <div>
                                <h4 className="font-medium">{employee.nombre}</h4>
                                <p className="text-sm text-gray-600">{employee.departamento}</p>
                                <div className="flex items-center space-x-2 mt-1">
                                  <Badge variant={employee.completed_assessments > 0 ? 'default' : 'secondary'}>
                                    {employee.completed_assessments} {t('reports.completed', 'completed')}
                                  </Badge>
                                  <span className="text-xs text-gray-500">
                                    {employee.total_assessments} {t('reports.total', 'total')}
                                  </span>
                                </div>
                              </div>
                            </div>
                            <Button 
                              size="sm" 
                              onClick={() => handleEmployeeSelection(employee.id)}
                              disabled={employee.completed_assessments === 0}
                            >
                              {language === 'es' ? 'Ver Gráfica' : 'View Chart'}
                            </Button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Employee Header Card with Info */}
              {individualRadarData && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center">
                          <Users className="h-5 w-5 mr-2" />
                          {language === 'es' ? 'Reporte Individual' : 'Individual Report'}
                        </CardTitle>
                        <CardDescription>
                          {individualRadarData.assessment_info?.usuario_nombre}
                        </CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="hide-on-pdf"
                        onClick={() => {
                          setIndividualRadarData(null);
                          setSelectedEmployee(null);
                          setEmployeeHRIndicators(null);
                          setInterpretation(null);
                        }}
                      >
                        {language === 'es' ? 'Cambiar Empleado' : 'Change Employee'}
                      </Button>
                    </div>
                    
                    {/* Employee Information */}
                    {selectedEmployee && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {(() => {
                          const employeeData = companyEmployees.find(emp => emp.id === selectedEmployee);
                          if (employeeData) {
                            return (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <div>
                                  <span className="font-semibold text-gray-700">{t('reports.name', 'Name')}: </span>
                                  <span className="text-gray-600">{`${employeeData.nombre || ''} ${employeeData.apellido || ''}`.trim()}</span>
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-700">{t('reports.email', 'Email')}: </span>
                                  <span className="text-gray-600">{employeeData.email || ''}</span>
                                </div>
                                {companyReport?.company?.nombre && (
                                  <div>
                                    <span className="font-semibold text-gray-700">{t('reports.company', 'Company')}: </span>
                                    <span className="text-gray-600">{companyReport.company.nombre}</span>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </CardHeader>
                </Card>
              )}

              {/* 1. HR Indicators Section (FIRST) */}
              {selectedEmployee && (
                <div className="pdf-section" style={{ pageBreakAfter: 'auto', pageBreakInside: 'avoid' }}>
                  <HRIndicatorsSection hrData={employeeHRIndicators} loading={loadingHRIndicators} />
                </div>
              )}

              {/* 2. Profile Charts - 3 columns layout with larger charts */}
              {individualRadarData && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
                      {language === 'es' ? 'Análisis de Compatibilidad' : 'Compatibility Analysis'}
                    </CardTitle>
                    <CardDescription>
                      {language === 'es' 
                        ? 'Comparación de ofertas y necesidades entre empleado y empresa'
                        : 'Comparison of offers and needs between employee and company'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Scores Summary - At the top */}
                    {individualRadarData.scores && (
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-sm font-medium text-blue-700 mb-1">
                            {language === 'es' ? 'Compatibilidad' : 'Compatibility'}
                          </p>
                          <p className="text-3xl font-bold text-blue-600">
                            {Math.round(individualRadarData.scores.compatibilidad || individualRadarData.scores.compatibility || 0)}%
                          </p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-sm font-medium text-green-700 mb-1">
                            {language === 'es' ? 'Balance' : 'Balance'}
                          </p>
                          <p className="text-3xl font-bold text-green-600">
                            {Math.round(individualRadarData.scores.balance || 0)}%
                          </p>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-100">
                          <p className="text-sm font-medium text-purple-700 mb-1">
                            {language === 'es' ? 'Crecimiento' : 'Growth'}
                          </p>
                          <p className="text-3xl font-bold text-purple-600">
                            {Math.round(individualRadarData.scores.potencial_crecimiento || individualRadarData.scores.growth || 0)}%
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Three charts in a row - unified style */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* 1. Employee Profile (Offers vs Needs) */}
                      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="p-3 border-b border-gray-100 bg-gray-50">
                          <h4 className="text-sm font-semibold text-center text-gray-700">
                            {language === 'es' ? 'Perfil del Empleado' : 'Employee Profile'}
                          </h4>
                          <p className="text-xs text-gray-500 text-center">
                            {language === 'es' ? 'Ofertas vs Necesidades' : 'Offers vs Needs'}
                          </p>
                        </div>
                        <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                          <IndividualRadarChart 
                            key={`profile-${selectedEmployee}-${language}`}
                            assessmentData={{
                              radar_chart: {
                                labels: language === 'es' 
                                  ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                  : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                datasets: individualRadarData.radar_chart?.datasets || []
                              },
                              scores: null
                            }}
                            compact={true}
                          />
                        </div>
                      </div>
                      
                      {/* 2. Employee Offer vs Company Need */}
                      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="p-3 border-b border-gray-100 bg-gray-50">
                          <h4 className="text-sm font-semibold text-center text-gray-700">
                            {language === 'es' ? 'Oferta Empleado vs Necesidad Empresa' : 'Employee Offer vs Company Need'}
                          </h4>
                          <p className="text-xs text-gray-500 text-center">
                            {language === 'es' ? '¿Qué ofrece el empleado?' : 'What does employee offer?'}
                          </p>
                        </div>
                        <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                          {companyProfileData?.chart_data ? (
                            <IndividualRadarChart 
                              key={`emp-offer-${selectedEmployee}-${language}`}
                              assessmentData={{
                                radar_chart: {
                                  labels: language === 'es' 
                                    ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                    : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                  datasets: [
                                    {
                                      label: language === 'es' ? 'Oferta Empleado' : 'Employee Offer',
                                      data: individualRadarData.radar_chart?.datasets?.[0]?.data || [],
                                      backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                      borderColor: 'rgba(59, 130, 246, 1)',
                                      borderWidth: 1,
                                      pointRadius: 3,
                                      pointBorderWidth: 1
                                    },
                                    {
                                      label: language === 'es' ? 'Necesidad Empresa' : 'Company Need',
                                      data: companyProfileData.chart_data.requerimientos || [],
                                      backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                      borderColor: 'rgba(239, 68, 68, 1)',
                                      borderWidth: 1,
                                      pointRadius: 3,
                                      pointBorderWidth: 1
                                    }
                                  ]
                                },
                                scores: null
                              }}
                              compact={true}
                            />
                          ) : (
                            <div className="text-gray-400 text-sm text-center">
                              {language === 'es' ? 'Perfil de empresa no disponible' : 'Company profile not available'}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* 3. Company Offer vs Employee Need */}
                      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="p-3 border-b border-gray-100 bg-gray-50">
                          <h4 className="text-sm font-semibold text-center text-gray-700">
                            {language === 'es' ? 'Oferta Empresa vs Necesidad Empleado' : 'Company Offer vs Employee Need'}
                          </h4>
                          <p className="text-xs text-gray-500 text-center">
                            {language === 'es' ? '¿Qué ofrece la empresa?' : 'What does company offer?'}
                          </p>
                        </div>
                        <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                          {companyProfileData?.chart_data ? (
                            <IndividualRadarChart 
                              key={`comp-offer-${selectedEmployee}-${language}`}
                              assessmentData={{
                                radar_chart: {
                                  labels: language === 'es' 
                                    ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                    : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                  datasets: [
                                    {
                                      label: language === 'es' ? 'Oferta Empresa' : 'Company Offer',
                                      data: companyProfileData.chart_data.ofertas || [],
                                      backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                      borderColor: 'rgba(34, 197, 94, 1)',
                                      borderWidth: 1,
                                      pointRadius: 3,
                                      pointBorderWidth: 1
                                    },
                                    {
                                      label: language === 'es' ? 'Necesidad Empleado' : 'Employee Need',
                                      data: individualRadarData.radar_chart?.datasets?.[1]?.data || [],
                                      backgroundColor: 'rgba(168, 85, 247, 0.2)',
                                      borderColor: 'rgba(168, 85, 247, 1)',
                                      borderWidth: 1,
                                      pointRadius: 3,
                                      pointBorderWidth: 1
                                    }
                                  ]
                                },
                                scores: null
                              }}
                              compact={true}
                            />
                          ) : (
                            <div className="text-gray-400 text-sm text-center">
                              {language === 'es' ? 'Perfil de empresa no disponible' : 'Company profile not available'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* 3. Profile Interpretations - Compact */}
              {selectedEmployee && interpretation && (
                <div className="pdf-section" style={{ pageBreakBefore: 'auto', pageBreakInside: 'avoid' }}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        📋 {language === 'es' ? 'Análisis del Perfil' : 'Profile Analysis'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="text-sm text-gray-700 space-y-2">
                        <ReportInterpretation 
                          interpretation={interpretation} 
                          userType="employee" 
                          compact={true}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* 4. Conclusions Section */}
              {selectedEmployee && (employeeHRIndicators || interpretation) && (
                <div className="pdf-section" style={{ pageBreakBefore: 'auto', pageBreakInside: 'avoid' }}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        📊 {language === 'es' ? 'Conclusiones' : 'Conclusions'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-4">
                      {/* HR Indicators Conclusion */}
                      {employeeHRIndicators && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h4 className="font-semibold text-sm text-gray-800 mb-2">
                            {language === 'es' ? 'Indicadores de Recursos Humanos' : 'HR Indicators'}
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {(() => {
                              const ind = employeeHRIndicators.indicators;
                              const irr = ind?.irr?.value || 0;
                              const ico = ind?.ico?.value || 0;
                              const iap = ind?.iap?.value || 0;
                              const ilo = ind?.ilo?.value || 0;
                              
                              if (language === 'es') {
                                let conclusion = '';
                                if (irr < 40) {
                                  conclusion += 'El empleado muestra un buen ajuste con las condiciones actuales de la empresa. ';
                                } else if (irr >= 60) {
                                  conclusion += 'Se detecta un desajuste significativo entre las necesidades del empleado y lo que la empresa ofrece. ';
                                }
                                
                                if (ico >= 60) {
                                  conclusion += 'Presenta un alto nivel de compromiso organizacional. ';
                                } else if (ico < 40) {
                                  conclusion += 'El compromiso organizacional podría fortalecerse. ';
                                }
                                
                                if (ilo >= 60) {
                                  conclusion += 'Existe un fuerte vínculo emocional con la organización.';
                                } else if (ilo < 40) {
                                  conclusion += 'Hay oportunidad de fortalecer la conexión cultural y emocional con la empresa.';
                                }
                                
                                return conclusion || 'Los indicadores muestran un perfil equilibrado con áreas de oportunidad identificadas.';
                              } else {
                                let conclusion = '';
                                if (irr < 40) {
                                  conclusion += 'The employee shows good alignment with current company conditions. ';
                                } else if (irr >= 60) {
                                  conclusion += 'A significant mismatch is detected between employee needs and company offerings. ';
                                }
                                
                                if (ico >= 60) {
                                  conclusion += 'Shows high organizational commitment. ';
                                } else if (ico < 40) {
                                  conclusion += 'Organizational commitment could be strengthened. ';
                                }
                                
                                if (ilo >= 60) {
                                  conclusion += 'There is a strong emotional bond with the organization.';
                                } else if (ilo < 40) {
                                  conclusion += 'There is opportunity to strengthen cultural and emotional connection with the company.';
                                }
                                
                                return conclusion || 'Indicators show a balanced profile with identified areas of opportunity.';
                              }
                            })()}
                          </p>
                        </div>
                      )}
                      
                      {/* Profile Conclusion */}
                      {interpretation && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h4 className="font-semibold text-sm text-gray-800 mb-2">
                            {language === 'es' ? 'Perfil de Dimensiones' : 'Dimensions Profile'}
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {(() => {
                              const scores = individualRadarData?.scores;
                              const compat = Math.round(scores?.compatibilidad || scores?.compatibility || 0);
                              const balance = Math.round(scores?.balance || 0);
                              const growth = Math.round(scores?.potencial_crecimiento || scores?.growth || 0);
                              
                              if (language === 'es') {
                                let conclusion = `Con una compatibilidad del ${compat}%, `;
                                if (compat >= 80) {
                                  conclusion += 'existe una excelente alineación entre lo que el empleado ofrece y lo que la empresa necesita. ';
                                } else if (compat >= 60) {
                                  conclusion += 'hay buena alineación general con algunas áreas de mejora. ';
                                } else {
                                  conclusion += 'se identifican oportunidades significativas para mejorar la alineación. ';
                                }
                                
                                if (balance >= 70) {
                                  conclusion += `El balance del ${balance}% indica un perfil equilibrado. `;
                                }
                                
                                if (growth >= 70) {
                                  conclusion += `El potencial de crecimiento del ${growth}% sugiere capacidad para asumir mayores responsabilidades.`;
                                } else if (growth < 50) {
                                  conclusion += `Se recomienda enfocarse en desarrollar áreas específicas para aumentar el potencial de crecimiento.`;
                                }
                                
                                return conclusion;
                              } else {
                                let conclusion = `With ${compat}% compatibility, `;
                                if (compat >= 80) {
                                  conclusion += 'there is excellent alignment between what the employee offers and what the company needs. ';
                                } else if (compat >= 60) {
                                  conclusion += 'there is good overall alignment with some areas for improvement. ';
                                } else {
                                  conclusion += 'significant opportunities to improve alignment are identified. ';
                                }
                                
                                if (balance >= 70) {
                                  conclusion += `The ${balance}% balance indicates a well-rounded profile. `;
                                }
                                
                                if (growth >= 70) {
                                  conclusion += `The ${growth}% growth potential suggests capacity for greater responsibilities.`;
                                } else if (growth < 50) {
                                  conclusion += `Focus on developing specific areas to increase growth potential is recommended.`;
                                }
                                
                                return conclusion;
                              }
                            })()}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* 5. HR Indicators Guide */}
              {selectedEmployee && employeeHRIndicators && (
                <div className="pdf-section" style={{ pageBreakBefore: 'auto', pageBreakInside: 'avoid' }}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        ℹ️ {language === 'es' ? 'Guía de Indicadores' : 'Indicators Guide'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-600">
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">IRR:</span>
                          <span>{language === 'es' 
                            ? 'Mide qué tan bien la empresa cubre las necesidades actuales del empleado. Bajo = mejor ajuste.' 
                            : 'Measures how well the company meets employee current needs. Lower = better fit.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">ICO:</span>
                          <span>{language === 'es' 
                            ? 'Evalúa el nivel de identificación y compromiso con los objetivos de la organización.' 
                            : 'Evaluates level of identification and commitment to organization goals.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">IAP:</span>
                          <span>{language === 'es' 
                            ? 'Indica qué tan bien las fortalezas del empleado coinciden con los requerimientos del puesto.' 
                            : 'Indicates how well employee strengths match job requirements.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">IPC:</span>
                          <span>{language === 'es' 
                            ? 'Evalúa la capacidad de desarrollo y crecimiento profesional dentro de la organización.' 
                            : 'Evaluates capacity for professional development and growth within the organization.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">ICF:</span>
                          <span>{language === 'es' 
                            ? 'Mide la compatibilidad entre los valores del empleado y la cultura organizacional.' 
                            : 'Measures compatibility between employee values and organizational culture.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">IPE:</span>
                          <span>{language === 'es' 
                            ? 'Predice el nivel de productividad esperado basado en competencias técnicas y habilidades.' 
                            : 'Predicts expected productivity level based on technical competencies and skills.'}</span>
                        </div>
                        <div className="flex items-start gap-2 md:col-span-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">ILO:</span>
                          <span>{language === 'es' 
                            ? 'Evalúa el vínculo emocional y la conexión del empleado con la misión y valores de la empresa.' 
                            : 'Evaluates emotional bond and employee connection with company mission and values.'}</span>
                        </div>
                      </div>
                      
                      {/* Scores Calculation Explanation */}
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2">
                          {language === 'es' ? 'Cálculo de Scores de Compatibilidad' : 'Compatibility Scores Calculation'}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600">
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-blue-600 min-w-[90px]">{language === 'es' ? 'Compatibilidad:' : 'Compatibility:'}</span>
                            <span>{language === 'es' 
                              ? 'Mide el ajuste entre lo que el empleado ofrece y lo que la empresa necesita. Se calcula comparando las ofertas del empleado con los requerimientos de la empresa en cada dimensión.' 
                              : 'Measures the fit between what the employee offers and what the company needs. Calculated by comparing employee offers with company requirements across dimensions.'}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-green-600 min-w-[90px]">{language === 'es' ? 'Balance:' : 'Balance:'}</span>
                            <span>{language === 'es' 
                              ? 'Evalúa la distribución equilibrada de capitales en el perfil. Un alto balance indica versatilidad y capacidad de adaptación a diferentes contextos.' 
                              : 'Evaluates the balanced distribution of capitals in the profile. High balance indicates versatility and adaptability to different contexts.'}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-purple-600 min-w-[90px]">{language === 'es' ? 'Crecimiento:' : 'Growth:'}</span>
                            <span>{language === 'es' 
                              ? 'Indica el potencial de desarrollo basado en las brechas identificadas entre ofertas y necesidades. Considera áreas de mejora y capacidad de expansión.' 
                              : 'Indicates development potential based on gaps identified between offers and needs. Considers improvement areas and expansion capacity.'}</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-400 mt-3 italic">
                        {language === 'es' 
                          ? 'Los indicadores y scores se calculan a partir del perfil de dimensiones (10 capitales) y la comparación con el perfil de la empresa.' 
                          : 'Indicators and scores are calculated from the dimensions profile (10 capitals) and comparison with the company profile.'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}
              
              </div> {/* End of employeeReportRef */}
            </div>
          </TabsContent>
          
          {/* Candidates Report */}
          <TabsContent value="candidates">
            <div className="space-y-6">
              {/* Export PDF Button - Only show when candidate is selected */}
              {selectedCandidate && candidateRadarData && (
                <div className="flex justify-end">
                  {canExportPDF ? (
                    <Button
                      onClick={exportCandidateReportToPDF}
                      disabled={exportingPDF}
                      variant="outline"
                      size="sm"
                      data-testid="export-candidate-pdf-btn"
                    >
                      <Download className="h-4 w-4 mr-2" />
                      {exportingPDF ? (language === 'es' ? 'Exportando...' : 'Exporting...') : (language === 'es' ? 'Exportar PDF' : 'Export PDF')}
                    </Button>
                  ) : (
                    <Button variant="outline" size="sm" disabled className="opacity-50">
                      <Lock className="h-4 w-4 mr-2" />
                      {language === 'es' ? 'PDF (Plan Superior)' : 'PDF (Upgrade Plan)'}
                    </Button>
                  )}
                </div>
              )}

              {/* Content to export */}
              <div ref={selectedCandidate ? candidateReportRef : null}>
                {/* Candidate Selection */}
                {!selectedCandidate && (
                <Card>
                  <CardHeader>
                    <CardTitle>{t('reports.select_candidate', 'Select Candidate')}</CardTitle>
                    <CardDescription>
                      {t('reports.select_candidate_desc', 'Select a candidate to view their assessment profile')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {candidates.length === 0 ? (
                      <div className="text-center py-8">
                        <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <p className="text-gray-500">
                          {t('reports.no_candidates', 'No candidates have completed assessments yet')}
                        </p>
                      </div>
                    ) : (
                      <div className="grid gap-3">
                        {candidates.filter(cand => cand.completed_assessments > 0).map((candidate) => (
                          <div key={candidate.id} className="p-4 border rounded-lg hover:bg-gray-50 transition-colors">
                            <div className="flex justify-between items-center">
                              <div className="flex items-center space-x-3">
                                <div>
                                  <h4 className="font-medium">{candidate.name}</h4>
                                  <p className="text-sm text-gray-600">{candidate.email}</p>
                                  <div className="flex items-center space-x-2 mt-1">
                                    <Badge variant={candidate.completed_assessments > 0 ? 'default' : 'secondary'}>
                                      {candidate.completed_assessments} {t('reports.completed', 'completed')}
                                    </Badge>
                                  </div>
                                </div>
                              </div>
                              <Button 
                                size="sm" 
                                onClick={() => handleCandidateSelection(candidate.id)}
                                disabled={candidate.completed_assessments === 0}
                              >
                                {language === 'es' ? 'Ver Gráfica' : 'View Chart'}
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              
              {/* Candidate Header Card */}
              {candidateRadarData && (
                <Card className="pdf-section">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="flex items-center">
                          <UserPlus className="h-5 w-5 mr-2" />
                          {language === 'es' ? 'Reporte de Candidato' : 'Candidate Report'}
                        </CardTitle>
                        <CardDescription>
                          {candidateRadarData.assessment_info?.usuario_nombre}
                        </CardDescription>
                      </div>
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="hide-on-pdf"
                        onClick={() => {
                          setCandidateRadarData(null);
                          setSelectedCandidate(null);
                          setCandidateInterpretation(null);
                          setCandidateHRIndicators(null);
                        }}
                      >
                        {language === 'es' ? 'Cambiar Candidato' : 'Change Candidate'}
                      </Button>
                    </div>
                    
                    {/* Candidate Information */}
                    {selectedCandidate && (
                      <div className="mt-4 pt-4 border-t border-gray-200">
                        {(() => {
                          const candidateData = candidates.find(cand => cand.id === selectedCandidate);
                          if (candidateData) {
                            return (
                              <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-sm">
                                <div>
                                  <span className="font-semibold text-gray-700">{t('reports.name', 'Name')}: </span>
                                  <span className="text-gray-600">{candidateData.name || ''}</span>
                                </div>
                                <div>
                                  <span className="font-semibold text-gray-700">{t('reports.email', 'Email')}: </span>
                                  <span className="text-gray-600">{candidateData.email || ''}</span>
                                </div>
                                {companyReport?.company?.nombre && (
                                  <div>
                                    <span className="font-semibold text-gray-700">{t('reports.company', 'Company')}: </span>
                                    <span className="text-gray-600">{companyReport.company.nombre}</span>
                                  </div>
                                )}
                              </div>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    )}
                  </CardHeader>
                </Card>
              )}

              {/* 1. HR Indicators for Candidate (FIRST) */}
              {selectedCandidate && (
                <div className="pdf-section" style={{ pageBreakAfter: 'auto', pageBreakInside: 'avoid' }}>
                  <HRIndicatorsSection hrData={candidateHRIndicators} loading={loadingHRIndicators} />
                </div>
              )}

              {/* 2. Profile Charts - 3 columns layout with larger charts (same as employee) */}
              {candidateRadarData && (
                <Card className="pdf-section">
                  <CardHeader>
                    <CardTitle className="flex items-center">
                      <BarChart3 className="h-5 w-5 mr-2" style={{ color: '#1A878D' }} />
                      {language === 'es' ? 'Análisis de Compatibilidad' : 'Compatibility Analysis'}
                    </CardTitle>
                    <CardDescription>
                      {language === 'es' 
                        ? 'Comparación de ofertas y necesidades entre candidato y empresa'
                        : 'Comparison of offers and needs between candidate and company'}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    {/* Scores Summary - At the top */}
                    {candidateRadarData.scores && (
                      <div className="grid grid-cols-3 gap-4 mb-6">
                        <div className="text-center p-4 bg-blue-50 rounded-lg border border-blue-100">
                          <p className="text-sm font-medium text-blue-700 mb-1">
                            {language === 'es' ? 'Compatibilidad' : 'Compatibility'}
                          </p>
                          <p className="text-3xl font-bold text-blue-600">
                            {Math.round(candidateRadarData.scores.compatibilidad || candidateRadarData.scores.compatibility || 0)}%
                          </p>
                        </div>
                        <div className="text-center p-4 bg-green-50 rounded-lg border border-green-100">
                          <p className="text-sm font-medium text-green-700 mb-1">
                            {language === 'es' ? 'Balance' : 'Balance'}
                          </p>
                          <p className="text-3xl font-bold text-green-600">
                            {Math.round(candidateRadarData.scores.balance || 0)}%
                          </p>
                        </div>
                        <div className="text-center p-4 bg-purple-50 rounded-lg border border-purple-100">
                          <p className="text-sm font-medium text-purple-700 mb-1">
                            {language === 'es' ? 'Crecimiento' : 'Growth'}
                          </p>
                          <p className="text-3xl font-bold text-purple-600">
                            {Math.round(candidateRadarData.scores.potencial_crecimiento || candidateRadarData.scores.growth || 0)}%
                          </p>
                        </div>
                      </div>
                    )}
                    
                    {/* Three charts in a row - unified style */}
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                      {/* 1. Candidate Profile (Offers vs Needs) */}
                      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="p-3 border-b border-gray-100 bg-gray-50">
                          <h4 className="text-sm font-semibold text-center text-gray-700">
                            {language === 'es' ? 'Perfil del Candidato' : 'Candidate Profile'}
                          </h4>
                          <p className="text-xs text-gray-500 text-center">
                            {language === 'es' ? 'Ofertas vs Necesidades' : 'Offers vs Needs'}
                          </p>
                        </div>
                        <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                          <IndividualRadarChart 
                            key={`profile-${selectedCandidate}-${language}`}
                            assessmentData={{
                              radar_chart: {
                                labels: language === 'es' 
                                  ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                  : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                datasets: candidateRadarData.radar_chart?.datasets || []
                              },
                              scores: null
                            }}
                            compact={true}
                          />
                        </div>
                      </div>
                      
                      {/* 2. Candidate Offer vs Company Need */}
                      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="p-3 border-b border-gray-100 bg-gray-50">
                          <h4 className="text-sm font-semibold text-center text-gray-700">
                            {language === 'es' ? 'Oferta Candidato vs Necesidad Empresa' : 'Candidate Offer vs Company Need'}
                          </h4>
                          <p className="text-xs text-gray-500 text-center">
                            {language === 'es' ? '¿Qué ofrece el candidato?' : 'What does candidate offer?'}
                          </p>
                        </div>
                        <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                          {companyProfileData?.chart_data ? (
                            <IndividualRadarChart 
                              key={`cand-offer-${selectedCandidate}-${language}`}
                              assessmentData={{
                                radar_chart: {
                                  labels: language === 'es' 
                                    ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                    : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                  datasets: [
                                    {
                                      label: language === 'es' ? 'Oferta Candidato' : 'Candidate Offer',
                                      data: candidateRadarData.radar_chart?.datasets?.[0]?.data || [],
                                      backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                      borderColor: 'rgba(59, 130, 246, 1)',
                                      borderWidth: 1,
                                      pointRadius: 3,
                                      pointBorderWidth: 1
                                    },
                                    {
                                      label: language === 'es' ? 'Necesidad Empresa' : 'Company Need',
                                      data: companyProfileData.chart_data.requerimientos || [],
                                      backgroundColor: 'rgba(239, 68, 68, 0.2)',
                                      borderColor: 'rgba(239, 68, 68, 1)',
                                      borderWidth: 1,
                                      pointRadius: 3,
                                      pointBorderWidth: 1
                                    }
                                  ]
                                },
                                scores: null
                              }}
                              compact={true}
                            />
                          ) : (
                            <div className="text-gray-400 text-sm text-center">
                              {language === 'es' ? 'Perfil de empresa no disponible' : 'Company profile not available'}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* 3. Company Offer vs Candidate Need */}
                      <div className="border border-gray-200 rounded-lg bg-white shadow-sm">
                        <div className="p-3 border-b border-gray-100 bg-gray-50">
                          <h4 className="text-sm font-semibold text-center text-gray-700">
                            {language === 'es' ? 'Oferta Empresa vs Necesidad Candidato' : 'Company Offer vs Candidate Need'}
                          </h4>
                          <p className="text-xs text-gray-500 text-center">
                            {language === 'es' ? '¿Qué ofrece la empresa?' : 'What does company offer?'}
                          </p>
                        </div>
                        <div className="p-2 flex items-center justify-center" style={{ minHeight: '300px' }}>
                          {companyProfileData?.chart_data ? (
                            <IndividualRadarChart 
                              key={`comp-offer-cand-${selectedCandidate}-${language}`}
                              assessmentData={{
                                radar_chart: {
                                  labels: language === 'es' 
                                    ? ['Físico', 'Humano', 'Manufacturado', 'Financiero', 'Natural', 'Cultural', 'Social', 'Conocimiento', 'Psicológico', 'Propósito']
                                    : ['Physical', 'Human', 'Manufactured', 'Financial', 'Natural', 'Cultural', 'Social', 'Knowledge', 'Psychological', 'Purpose'],
                                  datasets: [
                                    {
                                      label: language === 'es' ? 'Oferta Empresa' : 'Company Offer',
                                      data: companyProfileData.chart_data.ofertas || [],
                                      backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                      borderColor: 'rgba(34, 197, 94, 1)',
                                      borderWidth: 1,
                                      pointRadius: 3,
                                      pointBorderWidth: 1
                                    },
                                    {
                                      label: language === 'es' ? 'Necesidad Candidato' : 'Candidate Need',
                                      data: candidateRadarData.radar_chart?.datasets?.[1]?.data || [],
                                      backgroundColor: 'rgba(168, 85, 247, 0.2)',
                                      borderColor: 'rgba(168, 85, 247, 1)',
                                      borderWidth: 1,
                                      pointRadius: 3,
                                      pointBorderWidth: 1
                                    }
                                  ]
                                },
                                scores: null
                              }}
                              compact={true}
                            />
                          ) : (
                            <div className="text-gray-400 text-sm text-center">
                              {language === 'es' ? 'Perfil de empresa no disponible' : 'Company profile not available'}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* 3. Candidate Profile Interpretations - Compact */}
              {selectedCandidate && candidateInterpretation && (
                <div className="pdf-section" style={{ pageBreakBefore: 'auto', pageBreakInside: 'avoid' }}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        📋 {language === 'es' ? 'Análisis del Perfil' : 'Profile Analysis'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="text-sm text-gray-700 space-y-2">
                        <ReportInterpretation 
                          interpretation={candidateInterpretation} 
                          userType="candidate" 
                          compact={true}
                        />
                      </div>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* 4. Conclusions Section for Candidate */}
              {selectedCandidate && (candidateHRIndicators || candidateInterpretation) && (
                <div className="pdf-section" style={{ pageBreakBefore: 'auto', pageBreakInside: 'avoid' }}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        📊 {language === 'es' ? 'Conclusiones' : 'Conclusions'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 space-y-4">
                      {/* HR Indicators Conclusion for Candidate */}
                      {candidateHRIndicators && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h4 className="font-semibold text-sm text-gray-800 mb-2">
                            {language === 'es' ? 'Indicadores de Recursos Humanos' : 'HR Indicators'}
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {(() => {
                              const ind = candidateHRIndicators.indicators;
                              const ico = ind?.ico?.value || 0;
                              const iap = ind?.iap?.value || 0;
                              const icf = ind?.icf?.value || 0;
                              const ipe = ind?.ipe?.value || 0;
                              
                              if (language === 'es') {
                                let conclusion = '';
                                if (ico >= 60) {
                                  conclusion += 'El candidato muestra alto potencial de compromiso organizacional. ';
                                } else if (ico < 40) {
                                  conclusion += 'Se recomienda evaluar la alineación de valores durante el proceso de contratación. ';
                                }
                                
                                if (iap >= 70) {
                                  conclusion += 'Presenta excelente adecuación al puesto. ';
                                } else if (iap < 50) {
                                  conclusion += 'Podría requerir capacitación estructurada al inicio. ';
                                }
                                
                                if (icf >= 70) {
                                  conclusion += 'Alta compatibilidad cultural proyectada.';
                                } else if (icf < 50) {
                                  conclusion += 'Se sugiere un onboarding cultural estructurado.';
                                }
                                
                                return conclusion || 'Los indicadores muestran un perfil con potencial y áreas de desarrollo identificadas.';
                              } else {
                                let conclusion = '';
                                if (ico >= 60) {
                                  conclusion += 'The candidate shows high organizational commitment potential. ';
                                } else if (ico < 40) {
                                  conclusion += 'Recommend evaluating value alignment during hiring process. ';
                                }
                                
                                if (iap >= 70) {
                                  conclusion += 'Shows excellent job fit. ';
                                } else if (iap < 50) {
                                  conclusion += 'May require structured training at the start. ';
                                }
                                
                                if (icf >= 70) {
                                  conclusion += 'High projected cultural compatibility.';
                                } else if (icf < 50) {
                                  conclusion += 'Structured cultural onboarding is suggested.';
                                }
                                
                                return conclusion || 'Indicators show a profile with potential and identified development areas.';
                              }
                            })()}
                          </p>
                        </div>
                      )}
                      
                      {/* Profile Conclusion for Candidate */}
                      {candidateInterpretation && (
                        <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                          <h4 className="font-semibold text-sm text-gray-800 mb-2">
                            {language === 'es' ? 'Perfil de Dimensiones' : 'Dimensions Profile'}
                          </h4>
                          <p className="text-sm text-gray-600 leading-relaxed">
                            {(() => {
                              const scores = candidateRadarData?.scores;
                              const compat = Math.round(scores?.compatibilidad || scores?.compatibility || 0);
                              const balance = Math.round(scores?.balance || 0);
                              const growth = Math.round(scores?.potencial_crecimiento || scores?.growth || 0);
                              
                              if (language === 'es') {
                                let conclusion = `Con una compatibilidad proyectada del ${compat}%, `;
                                if (compat >= 80) {
                                  conclusion += 'existe excelente potencial de alineación entre lo que el candidato ofrece y lo que la empresa necesita. ';
                                } else if (compat >= 60) {
                                  conclusion += 'hay buena alineación potencial con algunas áreas a desarrollar durante la integración. ';
                                } else {
                                  conclusion += 'se recomienda evaluar cuidadosamente el ajuste al puesto. ';
                                }
                                
                                if (balance >= 70) {
                                  conclusion += `El balance del ${balance}% indica un perfil equilibrado. `;
                                }
                                
                                if (growth >= 70) {
                                  conclusion += `El potencial de crecimiento del ${growth}% sugiere capacidad para desarrollo futuro dentro de la organización.`;
                                } else if (growth < 50) {
                                  conclusion += `Se recomienda identificar áreas de desarrollo durante el proceso de onboarding.`;
                                }
                                
                                return conclusion;
                              } else {
                                let conclusion = `With ${compat}% projected compatibility, `;
                                if (compat >= 80) {
                                  conclusion += 'there is excellent potential alignment between what the candidate offers and what the company needs. ';
                                } else if (compat >= 60) {
                                  conclusion += 'there is good potential alignment with some areas to develop during integration. ';
                                } else {
                                  conclusion += 'careful evaluation of job fit is recommended. ';
                                }
                                
                                if (balance >= 70) {
                                  conclusion += `The ${balance}% balance indicates a well-rounded profile. `;
                                }
                                
                                if (growth >= 70) {
                                  conclusion += `The ${growth}% growth potential suggests capacity for future development within the organization.`;
                                } else if (growth < 50) {
                                  conclusion += `Identifying development areas during onboarding is recommended.`;
                                }
                                
                                return conclusion;
                              }
                            })()}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* 5. HR Indicators Guide for Candidate */}
              {selectedCandidate && candidateHRIndicators && (
                <div className="pdf-section" style={{ pageBreakBefore: 'auto', pageBreakInside: 'avoid' }}>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="flex items-center text-base">
                        ℹ️ {language === 'es' ? 'Guía de Indicadores' : 'Indicators Guide'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs text-gray-600">
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">ICO:</span>
                          <span>{language === 'es' 
                            ? 'Proyecta el nivel de compromiso que el candidato podría desarrollar con la organización.' 
                            : 'Projects the commitment level the candidate could develop with the organization.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">IAP:</span>
                          <span>{language === 'es' 
                            ? 'Indica qué tan bien las fortalezas del candidato coinciden con los requerimientos del puesto.' 
                            : 'Indicates how well candidate strengths match job requirements.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">IPC:</span>
                          <span>{language === 'es' 
                            ? 'Evalúa la capacidad del candidato para desarrollo y crecimiento dentro de la organización.' 
                            : 'Evaluates candidate capacity for development and growth within the organization.'}</span>
                        </div>
                        <div className="flex items-start gap-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">ICF:</span>
                          <span>{language === 'es' 
                            ? 'Proyecta la compatibilidad entre los valores del candidato y la cultura organizacional.' 
                            : 'Projects compatibility between candidate values and organizational culture.'}</span>
                        </div>
                        <div className="flex items-start gap-2 md:col-span-2">
                          <span className="font-semibold text-gray-700 min-w-[32px]">IPE:</span>
                          <span>{language === 'es' 
                            ? 'Predice el nivel de productividad esperado basado en las competencias técnicas y habilidades del candidato.' 
                            : 'Predicts expected productivity level based on candidate technical competencies and skills.'}</span>
                        </div>
                      </div>
                      
                      {/* Scores Calculation Explanation for Candidates */}
                      <div className="mt-4 pt-3 border-t border-gray-200">
                        <h5 className="text-xs font-semibold text-gray-700 mb-2">
                          {language === 'es' ? 'Cálculo de Scores de Compatibilidad' : 'Compatibility Scores Calculation'}
                        </h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600">
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-blue-600 min-w-[90px]">{language === 'es' ? 'Compatibilidad:' : 'Compatibility:'}</span>
                            <span>{language === 'es' 
                              ? 'Proyecta el ajuste entre lo que el candidato ofrece y lo que la empresa necesita. Se calcula comparando las ofertas del candidato con los requerimientos de la empresa.' 
                              : 'Projects the fit between what the candidate offers and what the company needs. Calculated by comparing candidate offers with company requirements.'}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-green-600 min-w-[90px]">{language === 'es' ? 'Balance:' : 'Balance:'}</span>
                            <span>{language === 'es' 
                              ? 'Evalúa la distribución equilibrada de capitales en el perfil del candidato. Un alto balance indica versatilidad y capacidad de adaptación.' 
                              : 'Evaluates the balanced distribution of capitals in the candidate profile. High balance indicates versatility and adaptability.'}</span>
                          </div>
                          <div className="flex items-start gap-2">
                            <span className="font-semibold text-purple-600 min-w-[90px]">{language === 'es' ? 'Crecimiento:' : 'Growth:'}</span>
                            <span>{language === 'es' 
                              ? 'Indica el potencial de desarrollo del candidato basado en las brechas identificadas. Considera áreas de mejora y capacidad de expansión post-contratación.' 
                              : 'Indicates candidate development potential based on identified gaps. Considers improvement areas and post-hiring expansion capacity.'}</span>
                          </div>
                        </div>
                      </div>
                      
                      <p className="text-xs text-gray-400 mt-3 italic">
                        {language === 'es' 
                          ? 'Los indicadores y scores se calculan como proyección en caso de contratación, basados en el perfil de dimensiones (10 capitales) y la comparación con el perfil de la empresa.' 
                          : 'Indicators and scores are calculated as a projection in case of hiring, based on dimensions profile (10 capitals) and comparison with company profile.'}
                      </p>
                    </CardContent>
                  </Card>
                </div>
              )}

              {/* Loading state for interpretations */}
              {selectedCandidate && candidateRadarData && !candidateInterpretation && (
                <Card>
                  <CardContent className="p-6">
                    <div className="flex items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin mr-2" style={{ color: '#1A878D' }} />
                      <p className="text-gray-500">
                        {language === 'es' ? 'Cargando interpretaciones...' : 'Loading interpretations...'}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              )}
              </div> {/* End of candidateReportRef */}
            </div>
          </TabsContent>
          
          {/* Trends Report - Hidden temporarily
          <TabsContent value="trends">
            <Card>
              <CardContent className="p-8 text-center">
                <TrendingUp className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 mb-4">
                  {t('reports.trends_analysis', 'Trends analysis and temporal evolution with circular charts')}
                </p>
                <p className="text-sm text-gray-400">
                  {t('reports.coming_soon', 'Coming soon')}
                </p>
              </CardContent>
            </Card>
          </TabsContent>
          */}

          {/* Advanced Comparison Tab */}
          <TabsContent value="advanced">
            <AdvancedComparisonSection 
              t={t}
            />
          </TabsContent>

        </Tabs>
      </main>
    </div>
  );
};

export default Reports;